/** Texts of the reference (the trading cheat sheet: candlestick patterns, structure, liquidity and SMC, confirmation, Fibonacci and risk, context): every entry's name, tag, short description, trigger, invalidation, lists and chart labels, and the expected market reaction. Each key in both languages. */
export const lexicon = {
  en: {
    "ws.acct.lexicon": "Reference",
    "lex.title": "Reference",
    "lex.subtitle": "Candlestick patterns · market structure · liquidity & SMC · confirmation · Fibonacci & risk · context",
    "lex.search": "Search a term …",
    "lex.allCats": "All topics",
    "lex.reset": "Reset",
    "lex.none": "No matching terms.",
    "lex.confirm": "✓ Counts from:",
    "lex.invalid": "✗ Invalid:",
    "lex.trendZigzag.name": "Trend direction (zigzag)",
    "lex.trendZigzag.tag": "Directional change, 3 ATR",
    "lex.trendZigzag.desc": "The trend as a zigzag of its turns: a rise lasts until price falls 3 ATR below the highest high since the last turn, then a fall until it rises 3 ATR above the lowest low. Confirmed turns never change.",
    "lex.trendZigzag.conf": "A new extreme in the trend's direction: the zigzag extends, the line “Trend turns at” follows.",
    "lex.trendZigzag.inv": "Price reaches the line “Trend turns at”: the trend turns.",
    "lex.trendZigzag.label.0": "high",
    "lex.trendZigzag.label.1": "turn",
    "lex.trendZigzag.level.0": "Trend turns at",
    "lex.trendZigzag.read.0.title": "The zigzag:",
    "lex.trendZigzag.read.0": "Each peak is a confirmed turn, the last stretch runs to the current extreme. When the last stretch rises, the trend is up. Example: turns at 3,950 (low) and $4,010 (high), now a new high at $4,030 — an uptrend.",
    "lex.trendZigzag.read.1.title": "Trend turns at:",
    "lex.trendZigzag.read.1": "The line lies 3 ATR below the high (in an uptrend) or above the low. It follows every new extreme. Example: high $4,030, ATR $5 — the line is at $4,015; if gold falls there, the downtrend holds.",
    "lex.trendZigzag.read.2.title": "Describes, does not forecast:",
    "lex.trendZigzag.read.2": "The zigzag recognises a new direction after about 7 bars on average. It says which trend the market is in, not where it goes — in a sideways market it flips often.",
    "lex.read": "How to read it:",
    "lex.movingAverages.label.0": "pullback holds",
    "lex.movingAverages.read.0.title": "Price vs. the average:",
    "lex.movingAverages.read.0": "Price above a rising average = uptrend, below a falling one = downtrend. Example: gold at $4,050, the EMA 50 rising at $4,020 — buyers are in control.",
    "lex.movingAverages.read.1.title": "Order:",
    "lex.movingAverages.read.1": "EMA 20 above EMA 50 above EMA 200 = a clean uptrend on all horizons; mixed up, there is no clear trend. Example: EMA 20 (4,040) > EMA 50 (4,020) > EMA 200 (3,950).",
    "lex.movingAverages.read.2.title": "Pullback:",
    "lex.movingAverages.read.2": "In a trend price often pulls back to the average and turns there (dynamic support). Example in the picture: the pullback touches the orange line and carries on up. A clear close below weakens the trend.",
    "lex.goldenDeathCross.read.0.title": "A late signal:",
    "lex.goldenDeathCross.read.0": "The cross confirms a trend already running — it comes after the move, not before. Example: gold rises from $3,800 to $4,000, only then does the EMA 50 cross the EMA 200 upwards.",
    "lex.goldenDeathCross.read.1.title": "Useless sideways:",
    "lex.goldenDeathCross.read.1": "In a sideways phase the lines cross back and forth. Example: three crosses in two weeks without direction — none of them means anything.",
    "lex.maRibbon.label.0": "tight & twisted",
    "lex.maRibbon.label.1": "fanned out",
    "lex.maRibbon.read.0.title": "Colours:",
    "lex.maRibbon.read.0": "Blue are the short averages (follow price quickly), red the long ones (slow). In the app 18 lines of 5 to 90 bars, fewer in the schematic picture.",
    "lex.maRibbon.read.1.title": "Order = direction:",
    "lex.maRibbon.read.1": "Blue above and red below = uptrend, blue below and red above = downtrend. Example on the right of the picture: price rises, the blue lines lie above the red ones.",
    "lex.maRibbon.read.2.title": "Width = strength:",
    "lex.maRibbon.read.2": "The wider the ribbon fans out, the stronger the trend. Tight and twisted as on the left of the picture means sideways, no direction — no trend trading there.",
    "lex.maRibbon.read.3.title": "Turn:",
    "lex.maRibbon.read.3": "When the blue lines run through the red ones and the ribbon contracts, the trend weakens or turns. Example: after a rise blue tips below red — the up move is over.",
    "lex.bollinger.name": "Bollinger Bands",
    "lex.bollinger.tag": "SMA 20 ± 2 standard deviations",
    "lex.bollinger.desc": "A band around the average of the last 20 closes, two standard deviations wide above and below: it shows what is “high” and “low” right now and how much price swings. About 90 % of closes lie inside.",
    "lex.bollinger.conf": "A close outside in a trend: a continuation first, not a reversal.",
    "lex.bollinger.inv": "Touching the band alone: no buy and no sell signal.",
    "lex.bollinger.label.0": "tight = calm",
    "lex.bollinger.label.1": "walking the band",
    "lex.bollinger.read.0.title": "Width = volatility:",
    "lex.bollinger.read.0": "Wide bands = price swings a lot, narrow bands = a calm market. Example in the picture: tight on the left, widening with the rise.",
    "lex.bollinger.read.1.title": "High at the top, low at the bottom:",
    "lex.bollinger.read.1": "At the upper band price is relatively high, at the lower relatively low — nothing more. Bollinger himself: “tags of the bands are just that, tags, not signals.” Example: gold tags the lower band at $3,980 and keeps falling.",
    "lex.bollinger.read.2.title": "Walking the band:",
    "lex.bollinger.read.2": "In a strong trend price walks along the upper (lower) band bar after bar. Example on the right of the picture: every close near the upper band — a strong uptrend, not “overbought”.",
    "lex.bollinger.read.3.title": "Middle line:",
    "lex.bollinger.read.3": "The dashed middle (SMA 20) is often where pullbacks turn in an uptrend; a clear close below it shows weakness.",
    "lex.bollingerSqueeze.read.0.title": "Calm before the move:",
    "lex.bollingerSqueeze.read.0": "The bands are as narrow as they have not been for long (in the app: at the low of the last 125 bars). Example: gold swings for three hours only between $4,010 and $4,016.",
    "lex.bollingerSqueeze.read.1.title": "Direction open:",
    "lex.bollingerSqueeze.read.1": "The squeeze says a move may come, not where. The direction shows with the first close outside the bands (“Squeeze ↑/↓”).",
    "lex.bollingerSqueeze.read.2.title": "False breakout:",
    "lex.bollingerSqueeze.read.2": "Often price first runs briefly the wrong way and then turns (Bollinger's “head fake”). Example: a close below the band, two bars later back inside and sharply up.",
    "lex.vwap.read.0.title": "Above or below:",
    "lex.vwap.read.0": "Price above the VWAP = the day's buyers are on average in profit, the day leans strong; below it leans weak. Example: VWAP $4,025, gold $4,040 — pullbacks to the VWAP tend to be bought.",
    "lex.vwap.read.1.title": "Band ±1σ:",
    "lex.vwap.read.1": "The lines above and below show one standard deviation from the VWAP. Far outside, price is stretched from the day's mean. Example: gold $15 above the upper band after news — chasing is expensive then.",
    "lex.vwap.read.2.title": "New every day:",
    "lex.vwap.read.2": "The VWAP starts anew each trading day (in the app 18:00 New York). Early on it is restless, it steadies during the day.",
    "lex.rsi.read.0.title": "70 / 30:",
    "lex.rsi.read.0": "Above 70 the move has risen strongly, below 30 fallen strongly. That is no reversal signal: in a trend the RSI stays there for long. Example: RSI 78, gold still rises another $30.",
    "lex.rsi.read.1.title": "Ranges in a trend (Cardwell):",
    "lex.rsi.read.1": "In an uptrend the RSI mostly swings between about 40 and 80, in a downtrend between 20 and 60. Example: in a rise the RSI turns up again at 42 — the trend holds.",
    "lex.rsi.read.2.title": "Signals in the app:",
    "lex.rsi.read.2": "The layer “Oscillator signals” marks where the RSI rises above 70 or falls below 30; the divergence layer shows where price and RSI part ways.",
    "lex.macd.read.0.title": "Line and signal:",
    "lex.macd.read.0": "MACD above its signal line = momentum rising, below = falling. Example: the MACD crosses its signal upwards after gold finished a pullback.",
    "lex.macd.read.1.title": "Zero line:",
    "lex.macd.read.1": "Above zero the EMA 12 is above the EMA 26 (short-term up), below zero beneath it. An upward cross above the zero line fits an uptrend.",
    "lex.macd.read.2.title": "Histogram:",
    "lex.macd.read.2": "The bars are the gap between MACD and signal. When they shrink, the move loses force even if price still rises.",
    "lex.stochastic.read.0.title": "80 / 20:",
    "lex.stochastic.read.0": "Above 80 price closes near the high of the last 14 bars, below 20 near the low. In a strong trend the stochastic stays above 80 or below 20 for long.",
    "lex.stochastic.read.1.title": "The two lines crossing:",
    "lex.stochastic.read.1": "%K crossing %D downwards above 80 = momentum turns down; upwards below 20 = turns up. The layer “Oscillator signals” marks these crosses.",
    "lex.stochastic.read.2.title": "Example:",
    "lex.stochastic.read.2": "Gold rises into a resistance zone, the stochastic is at 88 and %K crosses %D downwards — a hint the rise is tiring there, not yet an entry.",
    "lex.divergence.read.0.title": "Compare two highs:",
    "lex.divergence.read.0": "Price: the second high above the first; RSI: the second high lower. Example in the picture: 16.5 above 15 in price, but the indicator weaker — the rise loses force.",
    "lex.divergence.read.1.title": "Only with a break:",
    "lex.divergence.read.1": "A divergence alone is no entry; in strong trends many fail. Only a structure break the other way makes it a reversal.",
    "lex.hiddenDivergence.read.0.title": "Continuation, not reversal:",
    "lex.hiddenDivergence.read.0": "Price: a higher low; RSI: a lower low. The pullback was deep in the indicator, shallow in price — the uptrend is intact.",
    "lex.hiddenDivergence.read.1.title": "Example:",
    "lex.hiddenDivergence.read.1": "Gold makes lows at $3,990 and then $4,005, the RSI at 41 and then 36 — a buy with the trend is closer than a sell.",
    "lex.adx.read.0.title": "Strength, not direction:",
    "lex.adx.read.0": "The ADX only says how strong a trend is. Example: ADX 32 in a downtrend means a strong downtrend, not “buy”.",
    "lex.adx.read.1.title": "Rising or falling:",
    "lex.adx.read.1": "When the ADX rises above 25 the trend gains force; when it falls, it fades. +DI (above −DI: up) and −DI show the direction.",
    "lex.volumeProfile.read.0.title": "POC:",
    "lex.volumeProfile.read.0": "The price where most was traded — like a magnet. Example: the previous day's POC $4,020; when gold drifts today, it often returns there.",
    "lex.volumeProfile.read.1.title": "Value area:",
    "lex.volumeProfile.read.1": "The range holding 70 % of the volume (VAH above, VAL below): the “fair” range. Outside it price is rather out of balance.",
    "lex.volumeProfile.read.2.title": "Back into the range:",
    "lex.volumeProfile.read.2": "When price opens outside and moves back in, it often runs through to the other side. The app's day levels show the previous day's POC, VAH and VAL.",
    "lex.pivotPoints.read.0.title": "Above or below the pivot:",
    "lex.pivotPoints.read.0": "Above the pivot (P) the day leans strong, below it weak. Example: P $4,010, gold opens at $4,025 — R1 is the first target up.",
    "lex.pivotPoints.read.1.title": "R and S:",
    "lex.pivotPoints.read.1": "R1–R3 above (red) and S1–S3 below (green) are levels many watch — targets or places for a reaction, not sure turns.",
    "lex.heikinAshi.read.0.title": "Runs of colour:",
    "lex.heikinAshi.read.0": "Many green bars in a row = uptrend, many red = downtrend. Example: seven green Heikin Ashi bars although the normal candles look mixed.",
    "lex.heikinAshi.read.1.title": "Wicks:",
    "lex.heikinAshi.read.1": "Green bars without a lower wick = a strong rise; small bodies with wicks on both sides = indecision, often before a turn.",
    "lex.heikinAshi.read.2.title": "Not real prices:",
    "lex.heikinAshi.read.2": "The bars are computed from averages. Always set entry, stop and target at the real prices.",
    "lex.react.title": "Expected market reaction",
    "lex.react.dir.down": "bearish",
    "lex.react.dir.neutral": "neutral",
    "lex.react.dir.up": "bullish",
    "lex.react.dir.both": "either way",
    "lex.react.kind.reversal": "Reversal",
    "lex.react.kind.continuation": "Continuation",
    "lex.react.kind.breakout": "Breakout follows",
    "lex.react.kind.indecision": "Indecision",
    "lex.react.kind.reaction": "Reaction at the zone",
    "lex.react.kind.magnet": "Liquidity target",
    "lex.react.kind.volatility": "More movement",
    "lex.chart.price": "Price",
    "lex.chart.time": "Time →",
    "lex.chart.aria": "Schematic chart for {name}",
    "lex.intro.note": "Schematic examples according to the usual rulebooks. No pattern is an entry signal on its own; SMC terms are not fully standardised.",
    "lex.doji.name": "Doji",
    "lex.doji.tag": "Indecision",
    "lex.doji.desc": "Open ≈ close: neither side won.",
    "lex.doji.conf": "The next candle closes above the high (↑) or below the low (↓), best at an HTF zone.",
    "lex.doji.inv": "In the middle of a range or trend without a zone: just a pause.",
    "lex.doji.label.0": "Open ≈ Close",
    "lex.dragonflyGravestone.name": "Dragonfly & Gravestone Doji",
    "lex.dragonflyGravestone.tag": "Doji with rejection",
    "lex.dragonflyGravestone.desc": "Dragonfly: a long lower wick, lower prices rejected. Gravestone: a long upper wick, higher prices rejected.",
    "lex.dragonflyGravestone.conf": "The next candle closes above (dragonfly) or below (gravestone) the doji.",
    "lex.dragonflyGravestone.inv": "A close beyond the wick's extreme.",
    "lex.dragonflyGravestone.label.0": "Dragonfly",
    "lex.dragonflyGravestone.label.1": "Gravestone",
    "lex.hammer.name": "Hammer",
    "lex.hammer.tag": "Possible bullish reversal",
    "lex.hammer.desc": "A small body on top, a long lower wick after a push down: lower prices rejected.",
    "lex.hammer.conf": "The next candle closes above the hammer's high.",
    "lex.hammer.inv": "A close below the hammer's low.",
    "lex.hammer.label.0": "long lower wick",
    "lex.hangingMan.name": "Hanging Man",
    "lex.hangingMan.tag": "Possible bearish reversal",
    "lex.hangingMan.desc": "A hammer's shape after an uptrend: sellers already pushed price down clearly. Weaker than a shooting star.",
    "lex.hangingMan.conf": "The next candle closes below the body, better below the low.",
    "lex.hangingMan.inv": "A close above the high: the uptrend is intact.",
    "lex.hangingMan.label.0": "Hanging Man",
    "lex.invertedHammer.name": "Inverted Hammer",
    "lex.invertedHammer.tag": "Possible bullish reversal",
    "lex.invertedHammer.desc": "A small body at the bottom, a long upper wick after a downtrend: buyers pushed up for a while.",
    "lex.invertedHammer.conf": "The next candle closes above the body, better above the wick's high.",
    "lex.invertedHammer.inv": "The next candle closes below the low.",
    "lex.invertedHammer.label.0": "long upper wick",
    "lex.shootingStar.name": "Shooting Star",
    "lex.shootingStar.tag": "Possible bearish reversal",
    "lex.shootingStar.desc": "A small body at the bottom, a long upper wick after an uptrend: higher prices rejected.",
    "lex.shootingStar.conf": "The next candle closes below the low.",
    "lex.shootingStar.inv": "A close above the wick's high.",
    "lex.shootingStar.label.0": "upper wick",
    "lex.marubozu.name": "Marubozu",
    "lex.marubozu.tag": "Full control of one side",
    "lex.marubozu.desc": "A large body without wicks: one side in full control, often part of a displacement.",
    "lex.marubozu.conf": "The pullback reaches at most the body's middle, the low holds.",
    "lex.marubozu.inv": "A close below the body's middle.",
    "lex.marubozu.label.0": "no wick",
    "lex.spinningTop.name": "Spinning Top",
    "lex.spinningTop.tag": "Balance",
    "lex.spinningTop.desc": "A small body, long wicks on both sides: balance, in a trend often fading momentum.",
    "lex.spinningTop.conf": "The next candle closes above or below the range.",
    "lex.spinningTop.inv": "No reversal signal on its own.",
    "lex.spinningTop.label.0": "wicks above + below",
    "lex.bullEngulfing.name": "Bullish Engulfing",
    "lex.bullEngulfing.tag": "Bullish shift of momentum",
    "lex.bullEngulfing.desc": "A green body engulfs the previous red one: control passes to the buyers, strong at demand.",
    "lex.bullEngulfing.conf": "The next candle closes above the high; stronger with a BOS/CHoCH.",
    "lex.bullEngulfing.inv": "A close below the engulfing candle's low.",
    "lex.bullEngulfing.label.0": "green body engulfs red",
    "lex.bearEngulfing.name": "Bearish Engulfing",
    "lex.bearEngulfing.tag": "Bearish shift of momentum",
    "lex.bearEngulfing.desc": "A red body engulfs the previous green one: growing selling pressure after a rise.",
    "lex.bearEngulfing.conf": "The next candle closes below the low; stronger with a broken swing low.",
    "lex.bearEngulfing.inv": "A close above the engulfing candle's high.",
    "lex.bearEngulfing.label.0": "red body engulfs green",
    "lex.harami.name": "Harami (Bullish / Bearish)",
    "lex.harami.tag": "Inside candle after a large candle",
    "lex.harami.desc": "A small candle inside the previous large body: momentum fades, weaker than an engulfing.",
    "lex.harami.conf": "A close above (bullish) or below (bearish) the large candle's open.",
    "lex.harami.inv": "A close beyond the large candle's extreme.",
    "lex.harami.label.0": "Harami",
    "lex.piercingLine.name": "Piercing Line",
    "lex.piercingLine.tag": "Bullish two-candle pattern",
    "lex.piercingLine.desc": "After a strong red candle a green one closes above its body's middle.",
    "lex.piercingLine.conf": "The next candle closes above the red candle's open.",
    "lex.piercingLine.inv": "A close below the pattern's low.",
    "lex.piercingLine.label.0": "close above the body's middle",
    "lex.darkCloudCover.name": "Dark Cloud Cover",
    "lex.darkCloudCover.tag": "Bearish two-candle pattern",
    "lex.darkCloudCover.desc": "After a strong green candle a red one closes below its body's middle.",
    "lex.darkCloudCover.conf": "The next candle closes below the green candle's open.",
    "lex.darkCloudCover.inv": "A close above the red candle's high.",
    "lex.darkCloudCover.label.0": "close below the body's middle",
    "lex.tweezer.name": "Tweezer Bottom / Top",
    "lex.tweezer.tag": "Double rejection",
    "lex.tweezer.desc": "Two candles with the same low (bottom) or high (top): the level was defended twice.",
    "lex.tweezer.conf": "A close above the first candle's high (bottom).",
    "lex.tweezer.inv": "A close below the common low.",
    "lex.tweezer.level.0": "same low",
    "lex.morningStar.name": "Morning Star",
    "lex.morningStar.tag": "Bullish three-candle reversal",
    "lex.morningStar.desc": "A long red candle, a small transition candle, a strong green one: a reversal after a downtrend.",
    "lex.morningStar.conf": "The 3rd candle closes above the 1st one's middle.",
    "lex.morningStar.inv": "Price falls below the middle candle's low.",
    "lex.morningStar.label.0": "transition",
    "lex.eveningStar.name": "Evening Star",
    "lex.eveningStar.tag": "Bearish three-candle reversal",
    "lex.eveningStar.desc": "A long green candle, a small transition candle, a strong red one: a reversal after an uptrend.",
    "lex.eveningStar.conf": "The 3rd candle closes below the 1st one's middle.",
    "lex.eveningStar.inv": "Price rises above the middle candle's high.",
    "lex.eveningStar.label.0": "transition",
    "lex.soldiersCrows.name": "Three White Soldiers / Three Black Crows",
    "lex.soldiersCrows.tag": "Three strong trend candles",
    "lex.soldiersCrows.desc": "Three large candles in one direction, each closing near its extreme: a strong trend.",
    "lex.soldiersCrows.conf": "The pullback holds above the 2nd candle's middle, then a new high.",
    "lex.soldiersCrows.inv": "Long wicks on candles 2/3 or a close below the 3rd one's open.",
    "lex.insideBar.name": "Inside Bar",
    "lex.insideBar.tag": "Compression",
    "lex.insideBar.desc": "The whole candle lies within the mother candle's range: compression, a breakout follows.",
    "lex.insideBar.conf": "A close outside the mother candle, more reliable with the trend.",
    "lex.insideBar.inv": "Only a wick outside and a close back: fakeout.",
    "lex.insideBar.label.0": "mother candle",
    "lex.insideBar.label.1": "Inside Bar",
    "lex.outsideBar.name": "Outside Bar",
    "lex.outsideBar.tag": "Range expansion",
    "lex.outsideBar.desc": "The candle trades above the previous high and below the previous low: the range widens.",
    "lex.outsideBar.conf": "A close in the upper or lower third, the next candle confirms.",
    "lex.outsideBar.inv": "A close in the middle: indecision.",
    "lex.outsideBar.label.0": "higher high + lower low",
    "lex.swings.name": "Swing High / Swing Low",
    "lex.swings.tag": "The building block of every structure",
    "lex.swings.desc": "A high above the highs left and right (a low likewise): the building block of every structure.",
    "lex.swings.conf": "Confirmed only once the candles to its right have closed.",
    "lex.swings.inv": "The next candle exceeds the high.",
    "lex.swings.label.0": "Swing High",
    "lex.uptrend.name": "HH / HL – uptrend",
    "lex.uptrend.tag": "Bullish structure",
    "lex.uptrend.desc": "Higher highs and higher lows: a bullish structure as long as the sequence holds.",
    "lex.uptrend.conf": "A new HL counts only after a close above the last HH (BOS).",
    "lex.uptrend.inv": "A close below the last HL: a possible CHoCH.",
    "lex.uptrend.label.0": "HH",
    "lex.uptrend.label.1": "HL",
    "lex.uptrend.label.2": "HH",
    "lex.downtrend.name": "LH / LL – downtrend",
    "lex.downtrend.tag": "Bearish structure",
    "lex.downtrend.desc": "Lower highs and lower lows: a bearish structure until a structure point is regained.",
    "lex.downtrend.conf": "A new LH counts only after a close below the last LL.",
    "lex.downtrend.inv": "A close above the last LH: a possible CHoCH.",
    "lex.downtrend.label.0": "LH",
    "lex.downtrend.label.1": "LL",
    "lex.downtrend.label.2": "LH",
    "lex.downtrend.label.3": "LL",
    "lex.bos.name": "BOS – Break of Structure",
    "lex.bos.tag": "Structure break",
    "lex.bos.desc": "A close beyond a previous swing high (low): the trend runs on.",
    "lex.bos.conf": "A candle close, not just a wick; the pullback holds above the level.",
    "lex.bos.inv": "Only a wick through and a close back: rather a sweep.",
    "lex.bos.label.0": "BOS",
    "lex.bos.level.0": "previous swing high",
    "lex.choch.name": "CHoCH – Change of Character",
    "lex.choch.tag": "First possible change of trend",
    "lex.choch.desc": "A first break against the structure, e.g. a lower high in a downtrend: an early warning.",
    "lex.choch.conf": "Then a higher low that holds and a BOS in the new direction.",
    "lex.choch.inv": "A new low below the low before the CHoCH.",
    "lex.choch.label.0": "CHoCH",
    "lex.choch.level.0": "last lower high",
    "lex.mss.name": "MSS – Market Structure Shift",
    "lex.mss.tag": "Structure shift with displacement",
    "lex.mss.desc": "A break of the last relevant swing with displacement, often right after a sweep.",
    "lex.mss.conf": "A large close below the last higher low with a new FVG.",
    "lex.mss.inv": "A close back above the sweep's high.",
    "lex.mss.label.0": "Sweep",
    "lex.mss.label.1": "MSS",
    "lex.mss.level.0": "old high (BSL)",
    "lex.mss.level.1": "last higher low",
    "lex.internalExternal.name": "Internal vs. external structure",
    "lex.internalExternal.tag": "Which swing counts?",
    "lex.internalExternal.desc": "External swings bound the range, internal ones lie inside it. An internal break is no change of trend yet.",
    "lex.internalExternal.conf": "Only a close beyond the external structure confirms the trend.",
    "lex.internalExternal.inv": "An internal CHoCH without an external break: often just a pullback.",
    "lex.internalExternal.label.0": "internal swings",
    "lex.internalExternal.level.0": "external high",
    "lex.internalExternal.level.1": "external low",
    "lex.doubleTopBottom.name": "Double Top / Double Bottom",
    "lex.doubleTopBottom.tag": "Classic reversal pattern",
    "lex.doubleTopBottom.desc": "Two highs at a similar level with a neckline between, at the same time a liquidity pool.",
    "lex.doubleTopBottom.conf": "A close below the neckline, the retest as the entry.",
    "lex.doubleTopBottom.inv": "A close above the tops: sweep or breakout.",
    "lex.doubleTopBottom.label.0": "Top 1",
    "lex.doubleTopBottom.label.1": "Top 2",
    "lex.doubleTopBottom.level.0": "neckline",
    "lex.range.name": "Range / consolidation",
    "lex.range.tag": "Sideways market",
    "lex.range.desc": "Price swings between resistance and support.",
    "lex.range.conf": "A rejection at the edge and a close back into the range.",
    "lex.range.inv": "Several closes outside: the range is over.",
    "lex.range.level.0": "Resistance",
    "lex.range.level.1": "Support",
    "lex.buySellLiquidity.name": "BSL / SSL – buy-side & sell-side liquidity",
    "lex.buySellLiquidity.tag": "Liquidity above/below swings",
    "lex.buySellLiquidity.desc": "Buy-side liquidity rests above highs, sell-side below lows: stops gather there.",
    "lex.buySellLiquidity.conf": "After it is taken: a close back and a structure break the other way.",
    "lex.buySellLiquidity.inv": "Price holds beyond the level: a real breakout.",
    "lex.buySellLiquidity.level.0": "BSL – above highs",
    "lex.buySellLiquidity.level.1": "SSL – below lows",
    "lex.equalHighsLows.name": "EQH / EQL – equal highs / lows",
    "lex.equalHighsLows.tag": "Equal highs / lows",
    "lex.equalHighsLows.desc": "Equal highs or lows bundle visible liquidity, no automatic resistance.",
    "lex.equalHighsLows.conf": "A sweep with a close back and an LTF MSS the other way.",
    "lex.equalHighsLows.inv": "Clean closes beyond and the retest holds: breakout.",
    "lex.equalHighsLows.level.0": "EQH",
    "lex.equalHighsLows.level.1": "EQL",
    "lex.sweep.name": "Liquidity Sweep",
    "lex.sweep.tag": "Taking stops + return",
    "lex.sweep.desc": "A brief stab above a high (below a low), then a return: stops taken.",
    "lex.sweep.conf": "A close back on the old side, then displacement and an MSS.",
    "lex.sweep.inv": "The next candles close beyond and hold: breakout.",
    "lex.sweep.label.0": "Sweep",
    "lex.sweep.label.1": "close back",
    "lex.sweep.level.0": "swing high / liquidity",
    "lex.sessionLiquidity.name": "Session liquidity (PDH/PDL, Asia range)",
    "lex.sessionLiquidity.tag": "Time-based liquidity targets",
    "lex.sessionLiquidity.desc": "The previous day's, week's and session highs/lows are often taken at the London or NY open.",
    "lex.sessionLiquidity.conf": "A sweep of the level, a close back into the range, an LTF MSS in a killzone.",
    "lex.sessionLiquidity.inv": "Price holds outside the Asia range: a trend day.",
    "lex.sessionLiquidity.label.0": "London sweeps the Asia high",
    "lex.sessionLiquidity.level.0": "Asia High",
    "lex.sessionLiquidity.level.1": "Asia Low",
    "lex.fvgBull.name": "FVG – Fair Value Gap (bullish)",
    "lex.fvgBull.tag": "Three-candle imbalance",
    "lex.fvgBull.desc": "Candle 1's high below candle 3's low: a gap price can run back into.",
    "lex.fvgBull.conf": "Closes stay above the lower edge, a reaction candle closes out upwards.",
    "lex.fvgBull.inv": "A close below the lower edge: the FVG failed.",
    "lex.fvgBull.label.0": "candle 3",
    "lex.fvgBull.zone.0": "Bullish FVG: high C1 to low C3",
    "lex.fvgBear.name": "FVG – Fair Value Gap (bearish)",
    "lex.fvgBear.tag": "Three-candle imbalance",
    "lex.fvgBear.desc": "Candle 1's low above candle 3's high: an imbalance downwards.",
    "lex.fvgBear.conf": "Closes stay below the upper edge, a reaction candle closes out downwards.",
    "lex.fvgBear.inv": "A close above the upper edge: the FVG failed.",
    "lex.fvgBear.zone.0": "Bearish FVG: low C1 to high C3",
    "lex.ifvg.name": "iFVG – Inverse FVG",
    "lex.ifvg.tag": "An FVG changes its role",
    "lex.ifvg.desc": "A broken FVG changes its role: support becomes resistance (and the other way).",
    "lex.ifvg.conf": "A close through the whole FVG, a rejection on the retest.",
    "lex.ifvg.inv": "A close back through the zone.",
    "lex.ifvg.label.0": "break",
    "lex.ifvg.label.1": "Retest",
    "lex.ifvg.zone.0": "former bullish FVG",
    "lex.orderBlock.name": "OB – Order Block",
    "lex.orderBlock.tag": "Last opposite candle before an impulse",
    "lex.orderBlock.desc": "The last opposite candle before an impulse that breaks structure.",
    "lex.orderBlock.conf": "The impulse breaks structure (BOS), on the return a reaction with LTF confirmation.",
    "lex.orderBlock.inv": "A close beyond the OB: void, possibly a breaker.",
    "lex.orderBlock.label.0": "last red candle",
    "lex.orderBlock.zone.0": "possible bullish OB",
    "lex.breaker.name": "Breaker Block",
    "lex.breaker.tag": "A failed OB that changes its role",
    "lex.breaker.desc": "An order block broken after a sweep then acts from the other side.",
    "lex.breaker.conf": "A break with a close and displacement, the retest is rejected.",
    "lex.breaker.inv": "A close back through the zone.",
    "lex.breaker.label.0": "Sweep",
    "lex.breaker.label.1": "break",
    "lex.breaker.label.2": "retest",
    "lex.breaker.zone.0": "former bullish OB → resistance",
    "lex.mitigation.name": "Mitigation Block",
    "lex.mitigation.tag": "A failed OB without a sweep",
    "lex.mitigation.desc": "Like a breaker, but without a sweep: first a lower high, then the low breaks.",
    "lex.mitigation.conf": "On the retest a rejection and a close back below the zone.",
    "lex.mitigation.inv": "A close above the zone or the lower high.",
    "lex.mitigation.label.0": "Lower High",
    "lex.mitigation.label.1": "retest",
    "lex.mitigation.zone.0": "Mitigation Block",
    "lex.poi.name": "POI – Point of Interest",
    "lex.poi.tag": "An interesting price area",
    "lex.poi.desc": "A zone where a reaction is looked for: OB, FVG, swing level, supply/demand, liquidity. No signal yet.",
    "lex.poi.conf": "A rejection candle, a sweep in the zone or an LTF CHoCH.",
    "lex.poi.inv": "Run through without a reaction, a close beyond.",
    "lex.poi.zone.0": "POI zone",
    "lex.supplyDemand.name": "Supply & Demand",
    "lex.supplyDemand.tag": "Supply and demand zone",
    "lex.supplyDemand.desc": "Areas from which clear selling (supply) or buying pressure (demand) set in.",
    "lex.supplyDemand.conf": "A fresh zone, at the first test a rejection and an LTF structure shift.",
    "lex.supplyDemand.inv": "A close through the whole zone; every test weakens it.",
    "lex.supplyDemand.zone.0": "Supply",
    "lex.supplyDemand.zone.1": "Demand",
    "lex.displacement.name": "Displacement",
    "lex.displacement.tag": "An impulsive move",
    "lex.displacement.desc": "A fast move with large candles and little overlap, often leaving an FVG.",
    "lex.displacement.conf": "Large bodies, small wicks, breaks a swing level.",
    "lex.displacement.inv": "The move is taken back completely at once.",
    "lex.inducement.name": "Inducement (IDM)",
    "lex.inducement.tag": "A possible liquidity bait",
    "lex.inducement.desc": "A small intermediate swing as bait: price takes it before heading for the larger target.",
    "lex.inducement.conf": "Only after the IDM is taken does price react at the actual POI.",
    "lex.inducement.inv": "Who enters right at the IDM often becomes the liquidity.",
    "lex.inducement.label.0": "IDM",
    "lex.inducement.label.1": "IDM taken",
    "lex.inducement.label.2": "larger target",
    "lex.ote.name": "OTE – Optimal Trade Entry",
    "lex.ote.tag": "Fibonacci retracement area",
    "lex.ote.desc": "A pullback of about 62–79 % of an impulse that broke structure before.",
    "lex.ote.conf": "In the OTE a rejection or an LTF MSS follows.",
    "lex.ote.inv": "A close beyond 100 %.",
    "lex.ote.level.0": "approx. 62 %",
    "lex.ote.level.1": "approx. 79 %",
    "lex.ladder.name": "The confirmation ladder",
    "lex.ladder.tag": "From “zone reached” to “confirmed”",
    "lex.ladder.desc": "Confirmation is a sequence. The higher the step, the safer, but the worse the entry and reward/risk.",
    "lex.ladder.list.0.title": "Step 0 – zone reached:",
    "lex.ladder.list.0": "Price is at the POI (OB, FVG, supply/demand, liquidity). No signal yet.",
    "lex.ladder.list.1.title": "Step 1 – reaction:",
    "lex.ladder.list.1": "A wick/rejection, slowing down or a sweep within the zone.",
    "lex.ladder.list.2.title": "Step 2 – close confirmation:",
    "lex.ladder.list.2": "A candle closes on the right side (e.g. above the hammer's high, back into the range, above the sweep level).",
    "lex.ladder.list.3.title": "Step 3 – structure confirmation:",
    "lex.ladder.list.3": "An LTF CHoCH/MSS with displacement, ideally with a new FVG.",
    "lex.ladder.list.4.title": "Step 4 – follow-through:",
    "lex.ladder.list.4": "The retest of the broken level/FVG holds and the next swing runs in the new direction.",
    "lex.wickClose.name": "Wick vs. close",
    "lex.wickClose.tag": "When is a level really broken?",
    "lex.wickClose.desc": "A level is broken only when the body closes beyond it; a wick is just a probe.",
    "lex.wickClose.conf": "A close beyond; stricter: the next candle too, or the retest holds.",
    "lex.wickClose.inv": "A close back within a few candles.",
    "lex.wickClose.label.0": "wick only",
    "lex.wickClose.label.1": "close above",
    "lex.wickClose.level.0": "Level",
    "lex.signalCandle.name": "Signal and confirmation candle",
    "lex.signalCandle.tag": "Wait for the follow-through",
    "lex.signalCandle.desc": "The signal forms at the zone, the next candle confirms it.",
    "lex.signalCandle.conf": "A close above the signal's high, the stop below its low.",
    "lex.signalCandle.inv": "The confirmation candle closes below the signal's low.",
    "lex.signalCandle.label.0": "Signal",
    "lex.signalCandle.label.1": "confirmation",
    "lex.signalCandle.level.0": "signal high",
    "lex.breakRetest.name": "Break & Retest",
    "lex.breakRetest.tag": "Confirming the change of role",
    "lex.breakRetest.desc": "After the breakout price returns to the level; if it holds from the other side, the breakout is confirmed.",
    "lex.breakRetest.conf": "The retest is rejected with a wick, closes stay above the level.",
    "lex.breakRetest.inv": "A close back below the level: fakeout.",
    "lex.breakRetest.label.0": "break (close)",
    "lex.breakRetest.label.1": "retest holds",
    "lex.breakRetest.level.0": "old high → new support",
    "lex.sweepMssFvg.name": "Entry model: Sweep → MSS → FVG",
    "lex.sweepMssFvg.tag": "A typical SMC confirmation chain",
    "lex.sweepMssFvg.desc": "A sweep, then a displacement breaks the last swing (MSS) with an FVG, the entry on the return into the FVG.",
    "lex.sweepMssFvg.conf": "All four steps in this order, in the direction of the HTF bias.",
    "lex.sweepMssFvg.inv": "A close below the sweep's low or the FVG.",
    "lex.sweepMssFvg.label.0": "1 Sweep",
    "lex.sweepMssFvg.label.1": "2 MSS",
    "lex.sweepMssFvg.label.2": "3 Retest",
    "lex.sweepMssFvg.label.3": "4 Entry",
    "lex.sweepMssFvg.level.0": "SSL (old low)",
    "lex.sweepMssFvg.level.1": "MSS level",
    "lex.sweepMssFvg.zone.0": "FVG",
    "lex.ltfInHtfPoi.name": "LTF confirmation in the HTF POI",
    "lex.ltfInHtfPoi.tag": "HTF = where, LTF = when",
    "lex.ltfInHtfPoi.desc": "The higher timeframe decides where to trade, the lower one when.",
    "lex.ltfInHtfPoi.conf": "An LTF close above the last lower high, the entry on the retest.",
    "lex.ltfInHtfPoi.inv": "An LTF close below the HTF POI.",
    "lex.ltfInHtfPoi.label.0": "LTF CHoCH",
    "lex.ltfInHtfPoi.level.0": "last LTF lower high",
    "lex.ltfInHtfPoi.zone.0": "HTF POI (e.g. 4H demand)",
    "lex.volume.name": "Volume confirmation",
    "lex.volume.tag": "Participation behind the move",
    "lex.volume.desc": "Real breakouts often come with high volume; CFDs only have tick volume.",
    "lex.volume.conf": "A breakout candle with clearly more volume, the pullback with less.",
    "lex.volume.inv": "A breakout on weak volume and an immediate return.",
    "lex.volume.label.0": "high volume",
    "lex.volume.level.0": "Resistance",
    "lex.fakeout.name": "Fakeout – missing confirmation",
    "lex.fakeout.tag": "Typical false signals",
    "lex.fakeout.desc": "Only a wick or a narrow close above the level without a follow-up; the return is a signal the other way.",
    "lex.fakeout.conf": "A candle closes clearly on the old side again.",
    "lex.fakeout.inv": "The next candle runs on beyond: a real breakout.",
    "lex.fakeout.label.0": "weak breakout",
    "lex.fakeout.label.1": "close back",
    "lex.fakeout.level.0": "Resistance",
    "lex.checklist.name": "Confirmation checklist before the entry",
    "lex.checklist.tag": "Tick everything off, then trade",
    "lex.checklist.desc": "Fix beforehand which points are mandatory.",
    "lex.checklist.list.0": "HTF bias clear? (structure on 4H/1H, the last relevant swing holds)",
    "lex.checklist.list.1": "Price at an HTF POI, or in discount (long) or premium (short)?",
    "lex.checklist.list.2": "Liquidity taken before the reaction (sweep)?",
    "lex.checklist.list.3": "A reaction candle with a close on the right side?",
    "lex.checklist.list.4": "An LTF structure break (CHoCH/MSS) with displacement?",
    "lex.checklist.list.5": "An FVG/OB formed as the entry zone?",
    "lex.checklist.list.6": "Invalidation and stop clearly defined, reward/risk ≥ the planned minimum?",
    "lex.checklist.list.7": "No high-impact news soon, spread normal, in an active session?",
    "lex.premiumDiscount.name": "Premium / Discount / Equilibrium",
    "lex.premiumDiscount.tag": "Splitting the range in halves",
    "lex.premiumDiscount.desc": "Above a swing range's middle is premium, below it discount.",
    "lex.premiumDiscount.conf": "Long in discount, short in premium, each with a reaction and structure.",
    "lex.premiumDiscount.inv": "The range broke: draw a new range.",
    "lex.premiumDiscount.level.0": "50 % – Equilibrium",
    "lex.premiumDiscount.zone.0": "Premium",
    "lex.premiumDiscount.zone.1": "Discount",
    "lex.fibRetracement.name": "Fibonacci Retracement",
    "lex.fibRetracement.tag": "A swing's pullback",
    "lex.fibRetracement.desc": "How far price takes back an impulse: 38.2, 50, 61.8, 70.5, 78.6 %.",
    "lex.fibRetracement.conf": "A reaction at the level overlapping an OB/FVG/structure.",
    "lex.fibRetracement.inv": "A close beyond 100 %.",
    "lex.fibRetracement.level.0": "23.6 %",
    "lex.fibRetracement.level.1": "38.2 %",
    "lex.fibRetracement.level.2": "50.0 %",
    "lex.fibRetracement.level.3": "61.8 %",
    "lex.fibRetracement.level.4": "78.6 %",
    "lex.fibExtension.name": "Fibonacci Extension",
    "lex.fibExtension.tag": "Possible projected targets",
    "lex.fibExtension.desc": "Price targets beyond the swing, often 127.2 % and 161.8 %.",
    "lex.fibExtension.level.0": "161.8 %",
    "lex.fibExtension.level.1": "127.2 %",
    "lex.fibExtension.level.2": "100 %",
    "lex.atr.name": "ATR – Average True Range",
    "lex.atr.tag": "Volatility, not direction",
    "lex.atr.desc": "The average range per candle: shows how strongly price moves, not where.",
    "lex.stopTarget.name": "Stop loss / take profit",
    "lex.stopTarget.tag": "Fix the risk and the target beforehand",
    "lex.stopTarget.desc": "The stop at the invalidation, the target at the planned level, the size matching the risk.",
    "lex.stopTarget.level.0": "Take Profit",
    "lex.stopTarget.level.1": "Entry",
    "lex.stopTarget.level.2": "Stop Loss",
    "lex.positionSize.name": "Calculating the position size",
    "lex.positionSize.tag": "Risk in money → lots",
    "lex.positionSize.desc": "Position size = risk amount ÷ (stop distance × value per point).",
    "lex.positionSize.list.0": "Account 10,000 $, risk 1 % → 100 $",
    "lex.positionSize.list.1": "EUR/USD: entry 1.1000, stop 1.0980 → 20 pips",
    "lex.positionSize.list.2": "Pip value approx. 10 $ per standard lot (convert for a EUR account)",
    "lex.positionSize.list.3": "100 $ ÷ (20 × 10 $) = 0.5 lot",
    "lex.positionSize.list.4": "Include fees/spread; for indices/gold check the broker's point value.",
    "lex.rewardRisk.name": "Reward/risk & the hit rate needed",
    "lex.rewardRisk.tag": "Placing risk-reward correctly",
    "lex.rewardRisk.desc": "Hit rate needed without costs = 1 ÷ (1 + reward/risk).",
    "lex.rewardRisk.list.0": "Reward/risk 1 : 1 → 50 % hit rate needed",
    "lex.rewardRisk.list.1": "Reward/risk 1 : 2 → 33.3 %",
    "lex.rewardRisk.list.2": "Reward/risk 1 : 3 → 25 %",
    "lex.rewardRisk.list.3": "Reward/risk 1 : 5 → 16.7 %",
    "lex.rMultiple.name": "R multiple & break-even",
    "lex.rMultiple.tag": "Results in units of risk",
    "lex.rMultiple.desc": "1R is the planned risk; break-even moves the stop to the entry, costs remain.",
    "lex.rMultiple.level.0": "0R / Entry",
    "lex.rMultiple.level.1": "−1R",
    "lex.rMultiple.level.2": "+2R",
    "lex.topDown.name": "HTF / LTF – top-down",
    "lex.topDown.tag": "Analysing from large to small",
    "lex.topDown.desc": "The higher timeframe gives the context, the lower one the trigger and execution.",
    "lex.topDown.conf": "An LTF signal only in the HTF bias's direction or at an HTF POI.",
    "lex.topDown.inv": "An LTF signal against the HTF without a POI: low quality.",
    "lex.topDown.label.0": "HTF context",
    "lex.topDown.label.1": "LTF details",
    "lex.bias.name": "Market Bias",
    "lex.bias.tag": "The preferred market direction",
    "lex.bias.desc": "A working hypothesis, bullish, bearish or neutral, from structure and context, re-assessed regularly.",
    "lex.bias.conf": "Holds as long as the last relevant HTF swing holds.",
    "lex.bias.inv": "A close beyond this swing: neutral, re-assess.",
    "lex.bias.label.0": "bullish bias",
    "lex.killzones.name": "Sessions & killzones",
    "lex.killzones.tag": "When does the market move?",
    "lex.killzones.desc": "Most sweeps and displacements happen at session opens.",
    "lex.killzones.list.0": "Asia range: approx. 01:00–08:00 CET – often forms the high/low that is taken later",
    "lex.killzones.list.1": "London killzone: approx. 08:00–11:00 CET",
    "lex.killzones.list.2": "New York AM killzone: approx. 13:00–16:00 CET (US data often at 14:30, the stock market opens at 15:30)",
    "lex.killzones.list.3": "Times by ICT's definition in New York time – when the clocks change (the US and the EU not at the same time) they shift by an hour.",
    "lex.news.name": "News & high-impact events",
    "lex.news.tag": "When charts no longer count",
    "lex.news.desc": "Rate decisions, NFP, CPI can break levels in seconds and widen the spread. No new trade 15–30 min before and after.",
    "lex.confluence.name": "Confluence",
    "lex.confluence.tag": "Several hints coincide",
    "lex.confluence.desc": "Several independent hints raise the quality but guarantee nothing.",
    "lex.confluence.conf": "Independent factors: timeframe, zone, liquidity, structure, time. Three Fibonacci levels are not three reasons.",
    "lex.confluence.label.0": "several factors",
    "lex.invalidation.name": "Invalidation",
    "lex.invalidation.tag": "The setup's idea is no longer valid",
    "lex.invalidation.desc": "Fix beforehand which price refutes the idea, and do not move it afterwards.",
    "lex.invalidation.level.0": "invalidation level",
    "lex.spread.name": "Spread & slippage",
    "lex.spread.tag": "Execution costs",
    "lex.spread.desc": "Spread = ask − bid, slippage = the difference at the fill; both rise around news and in thin markets.",
    "lex.spread.level.0": "Ask",
    "lex.spread.level.1": "Bid",
    "lex.journal.name": "Backtesting & journal",
    "lex.journal.tag": "Measure rules instead of believing them",
    "lex.journal.desc": "A setup is only as good as its tested rules; judge only after 50–100 trades.",
    "lex.cat.candles": "Candlestick patterns",
    "lex.cat.structure": "Market structure",
    "lex.cat.smc": "Liquidity & SMC",
    "lex.cat.confirmation": "Recognising confirmation",
    "lex.cat.risk": "Fibonacci & risk",
    "lex.cat.context": "Context & execution",
    "lex.cat.patterns": "Chart patterns",
    "lex.cat.trend": "Trend & levels",
    "lex.cat.indicators": "Indicators",
    "lex.cat.phases": "Market phases & methods",
    "lex.cat.psychology": "Psychology",
    "lex.headShoulders.name": "Head & Shoulders",
    "lex.headShoulders.tag": "Reversal after an uptrend",
    "lex.headShoulders.desc": "Three highs, the middle one highest, the lows between them form the neckline.",
    "lex.headShoulders.conf": "A close below the neckline; a retest from below can be the entry.",
    "lex.headShoulders.inv": "A close above the right shoulder.",
    "lex.headShoulders.label.0": "shoulder",
    "lex.headShoulders.label.1": "head",
    "lex.headShoulders.label.2": "shoulder",
    "lex.headShoulders.level.0": "neckline",
    "lex.inverseHeadShoulders.name": "Inverse Head & Shoulders",
    "lex.inverseHeadShoulders.tag": "Reversal after a downtrend",
    "lex.inverseHeadShoulders.desc": "Three lows, the middle one lowest, the highs between them form the neckline.",
    "lex.inverseHeadShoulders.conf": "A close above the neckline; a retest from above can be the entry.",
    "lex.inverseHeadShoulders.inv": "A close below the right shoulder.",
    "lex.inverseHeadShoulders.label.0": "shoulder",
    "lex.inverseHeadShoulders.label.1": "head",
    "lex.inverseHeadShoulders.label.2": "shoulder",
    "lex.inverseHeadShoulders.level.0": "neckline",
    "lex.tripleTopBottom.name": "Triple Top / Bottom",
    "lex.tripleTopBottom.tag": "Rejected three times",
    "lex.tripleTopBottom.desc": "Three highs (lows) at the same level: it was defended three times. Shown here: triple top.",
    "lex.tripleTopBottom.conf": "A close below the neckline (the lows between).",
    "lex.tripleTopBottom.inv": "A close above the tops.",
    "lex.tripleTopBottom.label.0": "1",
    "lex.tripleTopBottom.label.1": "2",
    "lex.tripleTopBottom.label.2": "3",
    "lex.tripleTopBottom.level.0": "neckline",
    "lex.ascendingTriangle.name": "Ascending triangle",
    "lex.ascendingTriangle.tag": "Rising lows against a flat high",
    "lex.ascendingTriangle.desc": "Flat resistance, rising lows: buyers grow impatient, usually a breakout upwards.",
    "lex.ascendingTriangle.conf": "A close above the resistance, better with a retest.",
    "lex.ascendingTriangle.inv": "A close below the line of rising lows.",
    "lex.ascendingTriangle.label.0": "breakout",
    "lex.ascendingTriangle.level.0": "resistance",
    "lex.descendingTriangle.name": "Descending triangle",
    "lex.descendingTriangle.tag": "Falling highs against a flat low",
    "lex.descendingTriangle.desc": "Flat support, falling highs: sellers press, usually a breakout downwards.",
    "lex.descendingTriangle.conf": "A close below the support.",
    "lex.descendingTriangle.inv": "A close above the line of falling highs.",
    "lex.descendingTriangle.label.0": "breakout",
    "lex.descendingTriangle.level.0": "support",
    "lex.symmetricalTriangle.name": "Symmetrical triangle",
    "lex.symmetricalTriangle.tag": "Compression, direction open",
    "lex.symmetricalTriangle.desc": "Falling highs and rising lows converge; the breakout usually follows the trend.",
    "lex.symmetricalTriangle.conf": "A close outside the triangle, not just a wick.",
    "lex.symmetricalTriangle.inv": "A return into the triangle after the breakout: fakeout.",
    "lex.symmetricalTriangle.label.0": "breakout",
    "lex.flag.name": "Flag",
    "lex.flag.tag": "A short pause in the trend",
    "lex.flag.desc": "After a steep rise (the pole) a small channel against it; then the trend usually runs on.",
    "lex.flag.conf": "A close above the flag's upper line.",
    "lex.flag.inv": "A pullback deeper than half the pole.",
    "lex.flag.label.0": "pole",
    "lex.flag.label.1": "flag",
    "lex.pennant.name": "Pennant",
    "lex.pennant.tag": "A small triangle after the impulse",
    "lex.pennant.desc": "Like the flag, but the consolidation narrows into a small symmetrical triangle.",
    "lex.pennant.conf": "A close out of the pennant in the impulse's direction.",
    "lex.pennant.inv": "A breakout against the impulse.",
    "lex.pennant.label.0": "pennant",
    "lex.risingWedge.name": "Rising wedge",
    "lex.risingWedge.tag": "A tiring rise",
    "lex.risingWedge.desc": "Higher highs and lows, but the range narrows: the rise loses strength, often a break downwards follows.",
    "lex.risingWedge.conf": "A close below the wedge's lower line.",
    "lex.risingWedge.inv": "A close above the wedge's upper line.",
    "lex.fallingWedge.name": "Falling wedge",
    "lex.fallingWedge.tag": "A tiring fall",
    "lex.fallingWedge.desc": "Lower highs and lows, the range narrows: the fall loses strength, often a break upwards follows.",
    "lex.fallingWedge.conf": "A close above the wedge's upper line.",
    "lex.fallingWedge.inv": "A close below the wedge's lower line.",
    "lex.trendChannel.name": "Trend channel",
    "lex.trendChannel.tag": "A trend between two parallels",
    "lex.trendChannel.desc": "Highs and lows run between two parallel lines; in a rising channel the lower one is bought.",
    "lex.trendChannel.conf": "A reaction at the channel line with a close back into the channel.",
    "lex.trendChannel.inv": "A close outside the channel against the trend.",
    "lex.trendChannel.label.0": "channel",
    "lex.cupHandle.name": "Cup & Handle",
    "lex.cupHandle.tag": "A round base, then the breakout",
    "lex.cupHandle.desc": "A round base (the cup), then a small pullback (the handle) below the rim; the breakout above the rim continues the rise.",
    "lex.cupHandle.conf": "A close above the cup's rim.",
    "lex.cupHandle.inv": "A handle deeper than half the cup.",
    "lex.cupHandle.label.0": "cup",
    "lex.cupHandle.label.1": "handle",
    "lex.cupHandle.level.0": "rim",
    "lex.trendline.name": "Trend line",
    "lex.trendline.tag": "A line through the lows (highs)",
    "lex.trendline.desc": "In an uptrend drawn through at least two rising lows; from the third touch it counts as confirmed.",
    "lex.trendline.conf": "A reaction at the line, the close stays above it.",
    "lex.trendline.inv": "A close below the trend line.",
    "lex.trendline.label.0": "1",
    "lex.trendline.label.1": "2",
    "lex.trendline.label.2": "3",
    "lex.movingAverages.name": "Moving averages",
    "lex.movingAverages.tag": "EMA 20 / 50 / 200",
    "lex.movingAverages.desc": "The average price of the last n bars: above a rising average the trend is up, it often acts as dynamic support.",
    "lex.movingAverages.conf": "A pullback to the average holds, the close back above it.",
    "lex.movingAverages.inv": "A close below the average and it turns down.",
    "lex.goldenDeathCross.name": "Golden / Death cross",
    "lex.goldenDeathCross.tag": "Two averages crossing",
    "lex.goldenDeathCross.desc": "Golden cross: the fast average (e.g. 50) crosses the slow one (200) upwards; death cross downwards. A late trend signal.",
    "lex.goldenDeathCross.conf": "Price stays on the side of the cross.",
    "lex.goldenDeathCross.inv": "A quick cross back: a sideways phase.",
    "lex.maRibbon.name": "Moving average ribbon",
    "lex.maRibbon.tag": "18 EMAs 5–90 as one band",
    "lex.maRibbon.desc": "Many averages of growing length as one band (e.g. the Madrid ribbon: 18 EMAs of 5 to 90 bars): fanned out and parallel in a strong trend, short ones above the long ones upwards, below them downwards; close together and twisted when sideways.",
    "lex.maRibbon.conf": "The band widens in the trend's direction, a pullback turns before the long averages.",
    "lex.maRibbon.inv": "The short averages run through the long ones and the band contracts: the trend weakens or turns.",
    "lex.supportResistance.name": "Support / resistance",
    "lex.supportResistance.tag": "Where buyers or sellers wait",
    "lex.supportResistance.desc": "Price areas where price turned several times: buyers below (support), sellers above (resistance). Broken, they change roles.",
    "lex.supportResistance.conf": "A reaction at the level with a close back.",
    "lex.supportResistance.inv": "Clean closes beyond the level.",
    "lex.supportResistance.level.0": "resistance",
    "lex.supportResistance.level.1": "support",
    "lex.roundNumbers.name": "Round numbers",
    "lex.roundNumbers.tag": "Psychological levels",
    "lex.roundNumbers.desc": "Round prices (e.g. 4,000) attract orders and stops; price reacts there more often or runs on after the break.",
    "lex.roundNumbers.conf": "A close through the level (breakout) or a rejection with a wick (reaction).",
    "lex.roundNumbers.inv": "Swinging back and forth around it: no signal.",
    "lex.roundNumbers.level.0": "round number",
    "lex.pivotPoints.name": "Pivot points",
    "lex.pivotPoints.tag": "Day levels from the day before",
    "lex.pivotPoints.desc": "Pivot = (high + low + close) ÷ 3 of the day before, with R1/S1 above and below: many intraday traders watch them.",
    "lex.pivotPoints.conf": "A reaction at P, R1 or S1.",
    "lex.pivotPoints.inv": "Price runs through without a reaction.",
    "lex.pivotPoints.level.0": "R1",
    "lex.pivotPoints.level.1": "pivot",
    "lex.pivotPoints.level.2": "S1",
    "lex.gaps.name": "Price gap",
    "lex.gaps.tag": "A jump between two bars",
    "lex.gaps.desc": "Price opens clearly above (below) the last close, e.g. after the weekend; many gaps are filled later.",
    "lex.gaps.conf": "A pullback into the gap and a reaction at its edge.",
    "lex.gaps.inv": "Price runs on away from the gap: a breakaway gap.",
    "lex.gaps.zone.0": "gap",
    "lex.heikinAshi.name": "Heikin Ashi",
    "lex.heikinAshi.tag": "Smoothed candles",
    "lex.heikinAshi.desc": "Candles from averages: trends show as runs of one colour without a wick on the other side. The prices are not the real ones.",
    "lex.heikinAshi.conf": "Several candles of one colour without a wick on the other side.",
    "lex.heikinAshi.inv": "Small bodies with wicks on both sides: the trend fades.",
    "lex.rsi.name": "RSI",
    "lex.rsi.tag": "Relative strength 0–100",
    "lex.rsi.desc": "Measures the strength of the recent moves (usually 14 bars): above 70 overbought, below 30 oversold — in a trend it can stay there long.",
    "lex.rsi.conf": "A return out of the extreme together with a structure reaction.",
    "lex.rsi.inv": "Merely in the extreme zone: no reversal signal.",
    "lex.divergence.name": "Divergence",
    "lex.divergence.tag": "Price and indicator drift apart",
    "lex.divergence.desc": "Price makes a higher high, the indicator (RSI, MACD) a lower one: the rise loses strength. Shown here: bearish divergence.",
    "lex.divergence.conf": "A structure break the other way after the divergence.",
    "lex.divergence.inv": "The indicator catches up with the next high.",
    "lex.divergence.label.0": "high",
    "lex.divergence.label.1": "higher high · RSI lower",
    "lex.hiddenDivergence.name": "Hidden divergence",
    "lex.hiddenDivergence.tag": "A continuation divergence",
    "lex.hiddenDivergence.desc": "In an uptrend price makes a higher low, the indicator a lower one: the pullback is weak, the trend more likely runs on.",
    "lex.hiddenDivergence.conf": "A close above the pullback's last high.",
    "lex.hiddenDivergence.inv": "A close below the higher low.",
    "lex.macd.name": "MACD",
    "lex.macd.tag": "The gap between two averages",
    "lex.macd.desc": "The difference of EMA 12 and EMA 26 with a signal line (EMA 9): crosses and the zero line show momentum changing.",
    "lex.macd.conf": "A cross with the trend, backed by structure.",
    "lex.macd.inv": "Crosses in a sideways phase: mostly noise.",
    "lex.stochastic.name": "Stochastic",
    "lex.stochastic.tag": "The close within the range",
    "lex.stochastic.desc": "Where the close lies in the range of the last n bars (0–100): above 80 near the high, below 20 near the low.",
    "lex.stochastic.conf": "Its lines crossing back out of the extreme, at a zone.",
    "lex.stochastic.inv": "In a trend it stays in the extreme long.",
    "lex.vwap.name": "VWAP",
    "lex.vwap.tag": "The volume-weighted day average",
    "lex.vwap.desc": "The day's average price weighted by volume: above it the day's buyers are on average in profit, it acts as the day's balance.",
    "lex.vwap.conf": "A pullback to the VWAP holds with the trend.",
    "lex.vwap.inv": "Crossing it again and again: a sideways day.",
    "lex.vwap.level.0": "VWAP",
    "lex.bollingerSqueeze.name": "Bollinger squeeze",
    "lex.bollingerSqueeze.tag": "Narrow bands before the breakout",
    "lex.bollingerSqueeze.desc": "The bands (SMA 20 ± 2 standard deviations) contract: volatility is low, a breakout often lies ahead.",
    "lex.bollingerSqueeze.conf": "A close outside a band after the squeeze.",
    "lex.bollingerSqueeze.inv": "A return into the band: a false breakout.",
    "lex.bollingerSqueeze.label.0": "squeeze",
    "lex.adx.name": "ADX",
    "lex.adx.tag": "Trend strength, not direction",
    "lex.adx.desc": "Measures how strong a trend is (0–100): above 25 a trend, below more likely sideways. +DI and −DI show the direction.",
    "lex.adx.conf": "A rising ADX above 25 with the direction of +DI/−DI.",
    "lex.adx.inv": "A falling ADX: the trend fades.",
    "lex.volumeProfile.name": "Volume profile",
    "lex.volumeProfile.tag": "Volume per price",
    "lex.volumeProfile.desc": "Shows at which prices most was traded: the point of control (POC) and the value area (70 % of volume) act as magnet and levels.",
    "lex.volumeProfile.conf": "A reaction at the value area's edge or a return to the POC.",
    "lex.volumeProfile.inv": "A close outside the value area and acceptance there.",
    "lex.volumeProfile.level.0": "VAH",
    "lex.volumeProfile.level.1": "POC",
    "lex.volumeProfile.level.2": "VAL",
    "lex.marketPhases.name": "Trend, range, expansion",
    "lex.marketPhases.tag": "The market's phases",
    "lex.marketPhases.desc": "Markets alternate between sideways (range), a breakout with a large move (expansion) and trend; each phase needs other setups.",
    "lex.marketPhases.conf": "Range: trade the edges. Expansion/trend: pullbacks with the trend.",
    "lex.marketPhases.inv": "Trend setups in a range and the other way.",
    "lex.marketPhases.label.0": "range",
    "lex.marketPhases.label.1": "expansion",
    "lex.wyckoffAccumulation.name": "Wyckoff accumulation",
    "lex.wyckoffAccumulation.tag": "Buying in the range",
    "lex.wyckoffAccumulation.desc": "After a fall price swings sideways; a last stab below the low (spring) takes stops, then price breaks upwards (sign of strength).",
    "lex.wyckoffAccumulation.conf": "A spring with a close back into the range, then a close above the resistance.",
    "lex.wyckoffAccumulation.inv": "Closes below the spring's low.",
    "lex.wyckoffAccumulation.label.0": "spring",
    "lex.wyckoffAccumulation.label.1": "SOS",
    "lex.wyckoffAccumulation.level.0": "resistance",
    "lex.wyckoffDistribution.name": "Wyckoff distribution",
    "lex.wyckoffDistribution.tag": "Selling in the range",
    "lex.wyckoffDistribution.desc": "After a rise price swings sideways; a last stab above the high (upthrust) takes stops, then price breaks downwards (sign of weakness).",
    "lex.wyckoffDistribution.conf": "An upthrust with a close back into the range, then a close below the support.",
    "lex.wyckoffDistribution.inv": "Closes above the upthrust's high.",
    "lex.wyckoffDistribution.label.0": "upthrust",
    "lex.wyckoffDistribution.label.1": "SOW",
    "lex.wyckoffDistribution.level.0": "support",
    "lex.elliottWaves.name": "Elliott waves",
    "lex.elliottWaves.tag": "Five waves with the trend",
    "lex.elliottWaves.desc": "A trend in five waves (1, 3, 5 with it, 2 and 4 against it), then a correction in three. The count is often clear only in hindsight, the evidence is weak.",
    "lex.elliottWaves.conf": "Wave 4 stays above wave 1's high.",
    "lex.elliottWaves.inv": "Wave 2 falls below wave 1's start.",
    "lex.elliottWaves.label.0": "1",
    "lex.elliottWaves.label.1": "2",
    "lex.elliottWaves.label.2": "3",
    "lex.elliottWaves.label.3": "4",
    "lex.elliottWaves.label.4": "5",
    "lex.dailyLossLimit.name": "Daily loss limit",
    "lex.dailyLossLimit.tag": "Done for today",
    "lex.dailyLossLimit.desc": "A maximum loss per day fixed beforehand (e.g. 2 R); once reached, no more trades. Evidence: who raises the risk after losses does clearly worse.",
    "lex.drawdown.name": "Drawdown",
    "lex.drawdown.tag": "The fall from the peak",
    "lex.drawdown.desc": "The loss from the highest account balance to the lowest after it; good strategies have losing streaks too, the size must survive them.",
    "lex.riskOfRuin.name": "Risk of ruin",
    "lex.riskOfRuin.tag": "The probability of losing it all",
    "lex.riskOfRuin.desc": "How likely an account falls to zero at a given hit rate, reward/risk and risk per trade; small risks per trade lower it a lot.",
    "lex.riskOfRuin.list.0": "1 % per trade: even long losing streaks stay survivable",
    "lex.riskOfRuin.list.1": "5 % per trade: ten losses in a row cost about 40 %",
    "lex.correlation.name": "Correlation",
    "lex.correlation.tag": "When several trades are one",
    "lex.correlation.desc": "Simultaneous positions in related markets (e.g. gold long, DXY short) are a larger risk than it seems.",
    "lex.orderTypes.name": "Order types",
    "lex.orderTypes.tag": "How an order is filled",
    "lex.orderTypes.desc": "Market at once at the current price; limit only at the given price or better; stop becomes a market order when reached; stop-limit becomes a limit order.",
    "lex.orderTypes.list.0": "Market: surely filled, price open (slippage)",
    "lex.orderTypes.list.1": "Limit: price sure, the fill open",
    "lex.orderTypes.list.2": "Stop: trade a breakout or limit a loss",
    "lex.orderTypes.list.3": "Stop-limit: no fill at any price — at gaps maybe none at all",
    "lex.trailingStop.name": "Trailing stop",
    "lex.trailingStop.tag": "The stop moves along",
    "lex.trailingStop.desc": "The stop follows price at a fixed distance or below each new low; it locks in profit but also ends the trade on normal pullbacks.",
    "lex.trailingStop.level.0": "trailing stop",
    "lex.partialProfits.name": "Partial profits & scaling",
    "lex.partialProfits.tag": "Closing the position in parts",
    "lex.partialProfits.desc": "Part of the position closes at the first target, the rest runs on; it smooths the results but costs profit on large runs.",
    "lex.fomo.name": "FOMO",
    "lex.fomo.tag": "Fear of missing out",
    "lex.fomo.desc": "Entering because price is already running, not because the setup stands — mostly late, with a poor reward/risk.",
    "lex.revengeTrading.name": "Revenge trading",
    "lex.revengeTrading.tag": "Trying to win the loss back",
    "lex.revengeTrading.desc": "Trading bigger or without a setup right after a loss; studies show: exactly these traders do worst.",
    "lex.overtrading.name": "Overtrading",
    "lex.overtrading.tag": "Too many trades",
    "lex.overtrading.desc": "Trading from boredom or habit: every trade costs the spread, and weak setups dilute the good ones.",
    "lex.dispositionEffect.name": "Disposition effect",
    "lex.dispositionEffect.tag": "Winners too early, losers too late",
    "lex.dispositionEffect.desc": "The tendency to take profits quickly and let losses run; so the average loss grows larger than the average win.",
  },
  de: {
    "ws.acct.lexicon": "Nachschlagewerk",
    "lex.title": "Nachschlagewerk",
    "lex.subtitle": "Candlestick-Muster · Marktstruktur · Liquidity & SMC · Bestätigung · Fibonacci & Risiko · Kontext",
    "lex.search": "Begriff suchen …",
    "lex.allCats": "Alle Themen",
    "lex.reset": "Zurücksetzen",
    "lex.none": "Keine passenden Begriffe gefunden.",
    "lex.confirm": "✓ Gilt ab:",
    "lex.invalid": "✗ Ungültig:",
    "lex.trendZigzag.name": "Trendrichtung (Zickzack)",
    "lex.trendZigzag.tag": "Directional Change, 3 ATR",
    "lex.trendZigzag.desc": "Der Trend als Zickzack seiner Wenden: Ein Anstieg gilt, bis der Kurs 3 ATR unter das höchste Hoch seit der letzten Wende fällt, dann ein Fall, bis er 3 ATR über das tiefste Tief steigt. Bestätigte Wenden ändern sich nie mehr.",
    "lex.trendZigzag.conf": "Ein neues Extrem in Trendrichtung: der Zickzack verlängert sich, die Linie „Trendwende bei“ rückt nach.",
    "lex.trendZigzag.inv": "Der Kurs erreicht die Linie „Trendwende bei“: der Trend dreht.",
    "lex.trendZigzag.label.0": "Hoch",
    "lex.trendZigzag.label.1": "Wende",
    "lex.trendZigzag.level.0": "Trendwende bei",
    "lex.trendZigzag.read.0.title": "Der Zickzack:",
    "lex.trendZigzag.read.0": "Jede Spitze ist eine bestätigte Wende, das letzte Stück läuft zum aktuellen Extrem. Steigt das letzte Stück, ist der Trend aufwärts. Beispiel: Wenden bei 3.950 (Tief) und 4.010 $ (Hoch), jetzt neues Hoch 4.030 $ — Aufwärtstrend.",
    "lex.trendZigzag.read.1.title": "Trendwende bei:",
    "lex.trendZigzag.read.1": "Die Linie liegt 3 ATR unter dem Hoch (im Aufwärtstrend) bzw. über dem Tief. Sie rückt mit jedem neuen Extrem nach. Beispiel: Hoch 4.030 $, ATR 5 $ — die Linie liegt bei 4.015 $; fällt Gold dorthin, gilt der Abwärtstrend.",
    "lex.trendZigzag.read.2.title": "Beschreibt, sagt nicht voraus:",
    "lex.trendZigzag.read.2": "Eine neue Richtung erkennt der Zickzack im Mittel nach etwa 7 Kerzen. Er sagt, in welchem Trend der Markt steht, nicht wohin er läuft — im Seitwärtsmarkt wechselt er oft.",
    "lex.read": "So liest du es:",
    "lex.movingAverages.label.0": "Rücklauf hält",
    "lex.movingAverages.read.0.title": "Lage zum Durchschnitt:",
    "lex.movingAverages.read.0": "Kurs über einem steigenden Durchschnitt = Aufwärtstrend, unter einem fallenden = Abwärtstrend. Beispiel: Gold steht bei 4.050 $, der EMA 50 steigt bei 4.020 $ — die Käufer haben die Oberhand.",
    "lex.movingAverages.read.1.title": "Reihenfolge:",
    "lex.movingAverages.read.1": "EMA 20 über EMA 50 über EMA 200 = sauberer Aufwärtstrend auf allen Fristen; liegen sie durcheinander, gibt es keinen klaren Trend. Beispiel: EMA 20 (4.040) > EMA 50 (4.020) > EMA 200 (3.950).",
    "lex.movingAverages.read.2.title": "Rücklauf:",
    "lex.movingAverages.read.2": "Im Trend läuft der Kurs oft zum Durchschnitt zurück und dreht dort (dynamische Unterstützung). Beispiel im Bild: der Rücklauf berührt die orange Linie und läuft weiter nach oben. Ein Schluss klar darunter schwächt den Trend.",
    "lex.goldenDeathCross.read.0.title": "Spätes Signal:",
    "lex.goldenDeathCross.read.0": "Das Kreuz bestätigt einen Trend, der schon läuft — es kommt nach der Bewegung, nicht davor. Beispiel: Gold steigt von 3.800 auf 4.000 $, erst dann kreuzt der EMA 50 den EMA 200 nach oben.",
    "lex.goldenDeathCross.read.1.title": "Seitwärts wertlos:",
    "lex.goldenDeathCross.read.1": "In einer Seitwärtsphase kreuzen sich die Linien hin und her. Beispiel: drei Kreuze in zwei Wochen ohne Richtung — dann sagt keines etwas.",
    "lex.maRibbon.label.0": "eng & verdreht",
    "lex.maRibbon.label.1": "aufgefächert",
    "lex.maRibbon.read.0.title": "Farben:",
    "lex.maRibbon.read.0": "Blau sind die kurzen Durchschnitte (folgen dem Kurs schnell), Rot die langen (träge). In der App 18 Linien von 5 bis 90 Kerzen, im Bild schematisch weniger.",
    "lex.maRibbon.read.1.title": "Reihenfolge = Richtung:",
    "lex.maRibbon.read.1": "Blau oben und Rot unten = Aufwärtstrend, Blau unten und Rot oben = Abwärtstrend. Beispiel im Bild rechts: der Kurs steigt, die blauen Linien liegen über den roten.",
    "lex.maRibbon.read.2.title": "Breite = Stärke:",
    "lex.maRibbon.read.2": "Je weiter das Band aufgefächert ist, desto stärker der Trend. Eng und verdreht wie links im Bild heißt: seitwärts, keine Richtung — dort kein Trendhandel.",
    "lex.maRibbon.read.3.title": "Wende:",
    "lex.maRibbon.read.3": "Laufen die blauen Linien durch die roten und das Band zieht sich zusammen, lässt der Trend nach oder dreht. Beispiel: nach einem Anstieg kippt Blau unter Rot — die Aufwärtsbewegung ist vorbei.",
    "lex.bollinger.name": "Bollinger-Bänder",
    "lex.bollinger.tag": "SMA 20 ± 2 Standardabweichungen",
    "lex.bollinger.desc": "Ein Band um den Durchschnitt der letzten 20 Schlusskurse, zwei Standardabweichungen breit nach oben und unten: es zeigt, was gerade „hoch“ und „tief“ ist, und wie stark der Kurs schwankt. Rund 90 % der Schlusskurse liegen darin.",
    "lex.bollinger.conf": "Ein Schluss außerhalb im Trend: zuerst eine Fortsetzung, keine Umkehr.",
    "lex.bollinger.inv": "Ein Berühren des Bands allein: kein Kauf- und kein Verkaufssignal.",
    "lex.bollinger.label.0": "eng = ruhig",
    "lex.bollinger.label.1": "läuft am Band",
    "lex.bollinger.read.0.title": "Breite = Schwankung:",
    "lex.bollinger.read.0": "Breite Bänder = der Kurs schwankt stark, enge Bänder = ruhiger Markt. Beispiel im Bild: links eng, dann weiten sie sich mit dem Anstieg.",
    "lex.bollinger.read.1.title": "Oben hoch, unten tief:",
    "lex.bollinger.read.1": "Am oberen Band ist der Kurs relativ hoch, am unteren relativ tief — mehr nicht. Bollinger selbst: „Berührungen sind Berührungen, keine Signale.“ Beispiel: Gold tippt das untere Band bei 3.980 $ an und fällt weiter.",
    "lex.bollinger.read.2.title": "Am Band entlanglaufen:",
    "lex.bollinger.read.2": "Im starken Trend läuft der Kurs Kerze für Kerze am oberen (unteren) Band entlang. Beispiel im Bild rechts: jeder Schluss nahe am oberen Band — ein starker Aufwärtstrend, kein „überkauft“.",
    "lex.bollinger.read.3.title": "Mittellinie:",
    "lex.bollinger.read.3": "Die gestrichelte Mitte (SMA 20) ist im Aufwärtstrend oft die Stelle, an der Rückläufe drehen; ein Schluss klar darunter zeigt Schwäche.",
    "lex.bollingerSqueeze.read.0.title": "Ruhe vor der Bewegung:",
    "lex.bollingerSqueeze.read.0": "Die Bänder sind so eng wie seit langem nicht (in der App: am Tief der letzten 125 Kerzen). Beispiel: Gold pendelt drei Stunden nur zwischen 4.010 und 4.016 $.",
    "lex.bollingerSqueeze.read.1.title": "Richtung offen:",
    "lex.bollingerSqueeze.read.1": "Der Squeeze sagt, dass eine Bewegung kommen kann, nicht wohin. Die Richtung zeigt erst der erste Schluss außerhalb der Bänder („Squeeze ↑/↓“).",
    "lex.bollingerSqueeze.read.2.title": "Fehlausbruch:",
    "lex.bollingerSqueeze.read.2": "Oft läuft der Kurs erst kurz in die falsche Richtung und dreht dann (Bollingers „Head Fake“). Beispiel: Schluss unter dem Band, zwei Kerzen später zurück und kräftig nach oben.",
    "lex.vwap.read.0.title": "Über oder unter:",
    "lex.vwap.read.0": "Kurs über dem VWAP = die Käufer des Tages liegen im Schnitt im Plus, der Tag ist eher stark; darunter eher schwach. Beispiel: VWAP 4.025 $, Gold 4.040 $ — Rückläufe zum VWAP werden eher gekauft.",
    "lex.vwap.read.1.title": "Band ±1σ:",
    "lex.vwap.read.1": "Die Linien darüber und darunter zeigen eine Standardabweichung vom VWAP. Weit außerhalb ist der Kurs vom Tagesmittel gedehnt. Beispiel: Gold 15 $ über dem oberen Band nach einer Nachricht — Verfolgen ist dann teuer.",
    "lex.vwap.read.2.title": "Neu an jedem Tag:",
    "lex.vwap.read.2": "Der VWAP beginnt jeden Handelstag neu (in der App 18:00 New York). Am Morgen ist er daher noch unruhig, im Laufe des Tages wird er stabiler.",
    "lex.rsi.read.0.title": "70 / 30:",
    "lex.rsi.read.0": "Über 70 ist die Bewegung stark gestiegen, unter 30 stark gefallen. Das ist kein Umkehrsignal: im Trend bleibt der RSI lange dort. Beispiel: RSI 78, Gold steigt trotzdem weitere 30 $.",
    "lex.rsi.read.1.title": "Bereiche im Trend (Cardwell):",
    "lex.rsi.read.1": "Im Aufwärtstrend pendelt der RSI meist etwa zwischen 40 und 80, im Abwärtstrend zwischen 20 und 60. Beispiel: im Anstieg dreht der RSI bei 42 wieder hoch — der Trend hält.",
    "lex.rsi.read.2.title": "Signale in der App:",
    "lex.rsi.read.2": "Die Ebene „Oszillator-Signale“ markiert, wo der RSI über 70 steigt bzw. unter 30 fällt; die Divergenz-Ebene zeigt, wo Kurs und RSI auseinanderlaufen.",
    "lex.macd.read.0.title": "Linie und Signal:",
    "lex.macd.read.0": "MACD über seiner Signallinie = das Momentum steigt, darunter = es fällt. Beispiel: der MACD kreuzt die Signallinie nach oben, nachdem Gold einen Rücklauf beendet hat.",
    "lex.macd.read.1.title": "Nulllinie:",
    "lex.macd.read.1": "Über null liegt der EMA 12 über dem EMA 26 (kurzfristig aufwärts), unter null darunter. Ein Kreuz über der Nulllinie nach oben passt zum Aufwärtstrend.",
    "lex.macd.read.2.title": "Histogramm:",
    "lex.macd.read.2": "Die Säulen sind der Abstand zwischen MACD und Signal. Werden sie kleiner, verliert die Bewegung Kraft, auch wenn der Kurs noch steigt.",
    "lex.stochastic.read.0.title": "80 / 20:",
    "lex.stochastic.read.0": "Über 80 schließt der Kurs nahe am Hoch der letzten 14 Kerzen, unter 20 nahe am Tief. Im starken Trend bleibt die Stochastik lange über 80 bzw. unter 20.",
    "lex.stochastic.read.1.title": "Kreuz der zwei Linien:",
    "lex.stochastic.read.1": "%K kreuzt %D über 80 nach unten = das Momentum dreht nach unten; unter 20 nach oben = nach oben. Diese Kreuze markiert die Ebene „Oszillator-Signale“.",
    "lex.stochastic.read.2.title": "Beispiel:",
    "lex.stochastic.read.2": "Gold steigt in eine Widerstandszone, die Stochastik steht bei 88 und %K kreuzt %D nach unten — ein Hinweis, dass der Anstieg dort ermüdet, noch kein Einstieg.",
    "lex.divergence.read.0.title": "Zwei Hochs vergleichen:",
    "lex.divergence.read.0": "Kurs: zweites Hoch höher als das erste; RSI: zweites Hoch tiefer. Beispiel im Bild: 16,5 über 15 beim Kurs, der Indikator aber schwächer — der Anstieg verliert Kraft.",
    "lex.divergence.read.1.title": "Erst mit Bruch:",
    "lex.divergence.read.1": "Eine Divergenz allein ist kein Einstieg; in starken Trends scheitern viele. Erst ein Bruch der Struktur in Gegenrichtung macht daraus eine Umkehr.",
    "lex.hiddenDivergence.read.0.title": "Fortsetzung, keine Umkehr:",
    "lex.hiddenDivergence.read.0": "Kurs: höheres Tief; RSI: tieferes Tief. Der Rücklauf war im Indikator tief, im Kurs flach — der Aufwärtstrend ist intakt.",
    "lex.hiddenDivergence.read.1.title": "Beispiel:",
    "lex.hiddenDivergence.read.1": "Gold macht Tiefs bei 3.990 und dann 4.005 $, der RSI bei 41 und dann 36 — ein Kauf in Trendrichtung liegt näher als ein Verkauf.",
    "lex.adx.read.0.title": "Stärke, nicht Richtung:",
    "lex.adx.read.0": "Der ADX sagt nur, wie stark ein Trend ist. Beispiel: ADX 32 im Abwärtstrend heißt starker Abwärtstrend, nicht „kaufen“.",
    "lex.adx.read.1.title": "Steigend oder fallend:",
    "lex.adx.read.1": "Steigt der ADX über 25, gewinnt der Trend an Kraft; fällt er, lässt er nach. Die Richtung zeigen +DI (über −DI aufwärts) und −DI.",
    "lex.volumeProfile.read.0.title": "POC:",
    "lex.volumeProfile.read.0": "Der Preis, an dem am meisten gehandelt wurde — wie ein Magnet. Beispiel: POC des Vortags 4.020 $; läuft Gold heute ohne Richtung, kehrt es oft dorthin zurück.",
    "lex.volumeProfile.read.1.title": "Value Area:",
    "lex.volumeProfile.read.1": "Der Bereich mit 70 % des Volumens (VAH oben, VAL unten): der „faire“ Bereich. Außerhalb ist der Kurs eher im Ungleichgewicht.",
    "lex.volumeProfile.read.2.title": "Zurück in den Bereich:",
    "lex.volumeProfile.read.2": "Öffnet der Kurs außerhalb und läuft zurück hinein, läuft er oft durch bis zur anderen Seite. Die Tages-Level der App zeigen POC, VAH und VAL des Vortags.",
    "lex.pivotPoints.read.0.title": "Über oder unter dem Pivot:",
    "lex.pivotPoints.read.0": "Über dem Pivot (P) ist der Tag eher stark, darunter eher schwach. Beispiel: P 4.010 $, Gold eröffnet bei 4.025 $ — R1 ist das erste Ziel nach oben.",
    "lex.pivotPoints.read.1.title": "R und S:",
    "lex.pivotPoints.read.1": "R1–R3 darüber (rot) und S1–S3 darunter (grün) sind Level, auf die viele achten — Ziele oder Stellen für eine Reaktion, keine sicheren Wenden.",
    "lex.heikinAshi.read.0.title": "Farbfolgen:",
    "lex.heikinAshi.read.0": "Viele grüne Kerzen in Folge = Aufwärtstrend, viele rote = Abwärtstrend. Beispiel: sieben grüne Heikin-Ashi-Kerzen, obwohl die normalen Kerzen gemischt aussehen.",
    "lex.heikinAshi.read.1.title": "Dochte:",
    "lex.heikinAshi.read.1": "Grüne Kerzen ohne unteren Docht = starker Anstieg; kleine Körper mit Dochten auf beiden Seiten = Unentschlossenheit, oft vor einer Wende.",
    "lex.heikinAshi.read.2.title": "Keine echten Preise:",
    "lex.heikinAshi.read.2": "Die Kerzen sind aus Durchschnitten gerechnet. Einstieg, Stop und Ziel immer an den echten Kursen festmachen.",
    "lex.react.title": "Erwartete Marktreaktion",
    "lex.react.dir.down": "bärisch",
    "lex.react.dir.neutral": "neutral",
    "lex.react.dir.up": "bullisch",
    "lex.react.dir.both": "beide Richtungen",
    "lex.react.kind.reversal": "Umkehr",
    "lex.react.kind.continuation": "Fortsetzung",
    "lex.react.kind.breakout": "Ausbruch folgt",
    "lex.react.kind.indecision": "Unentschlossen",
    "lex.react.kind.reaction": "Reaktion an der Zone",
    "lex.react.kind.magnet": "Liquiditätsziel",
    "lex.react.kind.volatility": "Mehr Bewegung",
    "lex.chart.price": "Preis",
    "lex.chart.time": "Zeit →",
    "lex.chart.aria": "Schematischer Chart für {name}",
    "lex.intro.note": "Schematische Beispiele nach den üblichen Regelwerken. Kein Muster ist allein ein Einstiegssignal; SMC-Begriffe sind nicht einheitlich definiert.",
    "lex.doji.name": "Doji",
    "lex.doji.tag": "Unentschlossenheit",
    "lex.doji.desc": "Open ≈ Close: keine Seite hat gewonnen.",
    "lex.doji.conf": "Folgekerze schließt über dem Hoch (↑) oder unter dem Tief (↓), am besten an einer HTF-Zone.",
    "lex.doji.inv": "Mitten in Range oder Trend ohne Zone: nur eine Pause.",
    "lex.doji.label.0": "Open ≈ Close",
    "lex.dragonflyGravestone.name": "Dragonfly & Gravestone Doji",
    "lex.dragonflyGravestone.tag": "Doji mit Zurückweisung",
    "lex.dragonflyGravestone.desc": "Dragonfly: langer unterer Docht, tiefere Preise abgelehnt. Gravestone: langer oberer Docht, höhere Preise abgelehnt.",
    "lex.dragonflyGravestone.conf": "Folgekerze schließt über (Dragonfly) bzw. unter (Gravestone) dem Doji.",
    "lex.dragonflyGravestone.inv": "Schluss jenseits des Docht-Extrems.",
    "lex.dragonflyGravestone.label.0": "Dragonfly",
    "lex.dragonflyGravestone.label.1": "Gravestone",
    "lex.hammer.name": "Hammer",
    "lex.hammer.tag": "Mögliche bullische Umkehr",
    "lex.hammer.desc": "Kleiner Körper oben, langer unterer Docht nach einem Abwärtsschub: tiefere Preise abgelehnt.",
    "lex.hammer.conf": "Folgekerze schließt über dem Hammer-Hoch.",
    "lex.hammer.inv": "Schluss unter dem Hammer-Tief.",
    "lex.hammer.label.0": "langer unterer Docht",
    "lex.hangingMan.name": "Hanging Man",
    "lex.hangingMan.tag": "Mögliche bärische Umkehr",
    "lex.hangingMan.desc": "Hammer-Form nach einem Aufwärtstrend: Verkäufer drückten den Preis schon deutlich. Schwächer als ein Shooting Star.",
    "lex.hangingMan.conf": "Folgekerze schließt unter dem Körper, besser unter dem Tief.",
    "lex.hangingMan.inv": "Schluss über dem Hoch: Aufwärtstrend intakt.",
    "lex.hangingMan.label.0": "Hanging Man",
    "lex.invertedHammer.name": "Inverted Hammer",
    "lex.invertedHammer.tag": "Mögliche bullische Umkehr",
    "lex.invertedHammer.desc": "Kleiner Körper unten, langer oberer Docht nach einem Abwärtstrend: Käufer drückten zeitweise nach oben.",
    "lex.invertedHammer.conf": "Folgekerze schließt über dem Körper, besser über dem Docht-Hoch.",
    "lex.invertedHammer.inv": "Folgekerze schließt unter dem Tief.",
    "lex.invertedHammer.label.0": "langer oberer Docht",
    "lex.shootingStar.name": "Shooting Star",
    "lex.shootingStar.tag": "Mögliche bärische Umkehr",
    "lex.shootingStar.desc": "Kleiner Körper unten, langer oberer Docht nach einem Aufwärtstrend: höhere Preise abgelehnt.",
    "lex.shootingStar.conf": "Folgekerze schließt unter dem Tief.",
    "lex.shootingStar.inv": "Schluss über dem Docht-Hoch.",
    "lex.shootingStar.label.0": "oberer Docht",
    "lex.marubozu.name": "Marubozu",
    "lex.marubozu.tag": "Volle Kontrolle einer Seite",
    "lex.marubozu.desc": "Großer Körper ohne Dochte: eine Seite hat die volle Kontrolle, oft Teil eines Displacements.",
    "lex.marubozu.conf": "Rücklauf höchstens bis zur Körpermitte, das Tief hält.",
    "lex.marubozu.inv": "Schluss unter der Körpermitte.",
    "lex.marubozu.label.0": "kein Docht",
    "lex.spinningTop.name": "Spinning Top",
    "lex.spinningTop.tag": "Gleichgewicht",
    "lex.spinningTop.desc": "Kleiner Körper, lange Dochte beidseitig: Gleichgewicht, im Trend oft nachlassendes Momentum.",
    "lex.spinningTop.conf": "Folgekerze schließt über oder unter der Spanne.",
    "lex.spinningTop.inv": "Allein kein Umkehrsignal.",
    "lex.spinningTop.label.0": "Dochte oben + unten",
    "lex.bullEngulfing.name": "Bullish Engulfing",
    "lex.bullEngulfing.tag": "Bullischer Momentumwechsel",
    "lex.bullEngulfing.desc": "Grüner Körper umschließt den vorigen roten: Kontrollwechsel zu den Käufern, stark an Demand.",
    "lex.bullEngulfing.conf": "Folgekerze schließt über dem Hoch; stärker mit BOS/CHoCH.",
    "lex.bullEngulfing.inv": "Schluss unter dem Tief der Engulfing-Kerze.",
    "lex.bullEngulfing.label.0": "grüner Körper umschließt roten",
    "lex.bearEngulfing.name": "Bearish Engulfing",
    "lex.bearEngulfing.tag": "Bärischer Momentumwechsel",
    "lex.bearEngulfing.desc": "Roter Körper umschließt den vorigen grünen: wachsender Verkaufsdruck nach einem Anstieg.",
    "lex.bearEngulfing.conf": "Folgekerze schließt unter dem Tief; stärker mit Bruch eines Swing-Tiefs.",
    "lex.bearEngulfing.inv": "Schluss über dem Hoch der Engulfing-Kerze.",
    "lex.bearEngulfing.label.0": "roter Körper umschließt grünen",
    "lex.harami.name": "Harami (Bullish / Bearish)",
    "lex.harami.tag": "Innenkerze nach großer Kerze",
    "lex.harami.desc": "Kleine Kerze im Körper der vorigen großen: das Momentum lässt nach, schwächer als ein Engulfing.",
    "lex.harami.conf": "Schluss über (bullisch) bzw. unter (bärisch) dem Open der großen Kerze.",
    "lex.harami.inv": "Schluss jenseits des Extrems der großen Kerze.",
    "lex.harami.label.0": "Harami",
    "lex.piercingLine.name": "Piercing Line",
    "lex.piercingLine.tag": "Bullische Zweikerzen-Formation",
    "lex.piercingLine.desc": "Nach einer starken roten Kerze schließt eine grüne über deren Körpermitte.",
    "lex.piercingLine.conf": "Folgekerze schließt über dem Open der roten Kerze.",
    "lex.piercingLine.inv": "Schluss unter dem Tief der Formation.",
    "lex.piercingLine.label.0": "Close über Körpermitte",
    "lex.darkCloudCover.name": "Dark Cloud Cover",
    "lex.darkCloudCover.tag": "Bärische Zweikerzen-Formation",
    "lex.darkCloudCover.desc": "Nach einer starken grünen Kerze schließt eine rote unter deren Körpermitte.",
    "lex.darkCloudCover.conf": "Folgekerze schließt unter dem Open der grünen Kerze.",
    "lex.darkCloudCover.inv": "Schluss über dem Hoch der roten Kerze.",
    "lex.darkCloudCover.label.0": "Close unter Körpermitte",
    "lex.tweezer.name": "Tweezer Bottom / Top",
    "lex.tweezer.tag": "Doppelte Zurückweisung",
    "lex.tweezer.desc": "Zwei Kerzen mit gleichem Tief (Bottom) bzw. Hoch (Top): das Level wurde zweimal verteidigt.",
    "lex.tweezer.conf": "Schluss über dem Hoch der ersten Kerze (Bottom).",
    "lex.tweezer.inv": "Schluss unter dem gemeinsamen Tief.",
    "lex.tweezer.level.0": "gleiches Tief",
    "lex.morningStar.name": "Morning Star",
    "lex.morningStar.tag": "Bullische Dreikerzen-Umkehr",
    "lex.morningStar.desc": "Lange rote Kerze, kleine Übergangskerze, kräftige grüne: Umkehr nach einem Abwärtstrend.",
    "lex.morningStar.conf": "Die 3. Kerze schließt über der Mitte der 1.",
    "lex.morningStar.inv": "Kurs fällt unter das Tief der mittleren Kerze.",
    "lex.morningStar.label.0": "Übergang",
    "lex.eveningStar.name": "Evening Star",
    "lex.eveningStar.tag": "Bärische Dreikerzen-Umkehr",
    "lex.eveningStar.desc": "Lange grüne Kerze, kleine Übergangskerze, kräftige rote: Umkehr nach einem Aufwärtstrend.",
    "lex.eveningStar.conf": "Die 3. Kerze schließt unter der Mitte der 1.",
    "lex.eveningStar.inv": "Kurs steigt über das Hoch der mittleren Kerze.",
    "lex.eveningStar.label.0": "Übergang",
    "lex.soldiersCrows.name": "Three White Soldiers / Three Black Crows",
    "lex.soldiersCrows.tag": "Drei starke Trendkerzen",
    "lex.soldiersCrows.desc": "Drei große Kerzen in eine Richtung, jede schließt nahe ihrem Extrem: starker Trend.",
    "lex.soldiersCrows.conf": "Rücklauf hält über der Mitte der 2. Kerze, dann neues Hoch.",
    "lex.soldiersCrows.inv": "Lange Dochte an Kerze 2/3 oder Schluss unter dem Open der 3.",
    "lex.insideBar.name": "Inside Bar",
    "lex.insideBar.tag": "Kompression",
    "lex.insideBar.desc": "Die ganze Kerze liegt in der Spanne der Mutterkerze: Kompression, Ausbruch folgt.",
    "lex.insideBar.conf": "Schluss außerhalb der Mutterkerze, in Trendrichtung zuverlässiger.",
    "lex.insideBar.inv": "Nur ein Docht hinaus und Schluss zurück: Fakeout.",
    "lex.insideBar.label.0": "Mutterkerze",
    "lex.insideBar.label.1": "Inside Bar",
    "lex.outsideBar.name": "Outside Bar",
    "lex.outsideBar.tag": "Range Expansion",
    "lex.outsideBar.desc": "Die Kerze handelt über das vorige Hoch und unter das vorige Tief: die Spanne weitet sich.",
    "lex.outsideBar.conf": "Schluss im oberen bzw. unteren Drittel, Folgekerze bestätigt.",
    "lex.outsideBar.inv": "Schluss in der Mitte: Unentschlossenheit.",
    "lex.outsideBar.label.0": "High höher + Low tiefer",
    "lex.swings.name": "Swing High / Swing Low",
    "lex.swings.tag": "Grundbaustein jeder Struktur",
    "lex.swings.desc": "Hoch über den Hochs links und rechts (Tief analog): Grundbaustein jeder Struktur.",
    "lex.swings.conf": "Bestätigt erst, wenn die Kerzen rechts geschlossen sind.",
    "lex.swings.inv": "Die nächste Kerze überbietet das Hoch.",
    "lex.swings.label.0": "Swing High",
    "lex.uptrend.name": "HH / HL – Aufwärtstrend",
    "lex.uptrend.tag": "Bullische Struktur",
    "lex.uptrend.desc": "Höhere Hochs und höhere Tiefs: bullische Struktur, solange die Folge hält.",
    "lex.uptrend.conf": "Ein neues HL gilt erst nach Schluss über dem letzten HH (BOS).",
    "lex.uptrend.inv": "Schluss unter dem letzten HL: möglicher CHoCH.",
    "lex.uptrend.label.0": "HH",
    "lex.uptrend.label.1": "HL",
    "lex.uptrend.label.2": "HH",
    "lex.downtrend.name": "LH / LL – Abwärtstrend",
    "lex.downtrend.tag": "Bärische Struktur",
    "lex.downtrend.desc": "Tiefere Hochs und tiefere Tiefs: bärische Struktur, bis ein Strukturpunkt zurückerobert wird.",
    "lex.downtrend.conf": "Ein neues LH gilt erst nach Schluss unter dem letzten LL.",
    "lex.downtrend.inv": "Schluss über dem letzten LH: möglicher CHoCH.",
    "lex.downtrend.label.0": "LH",
    "lex.downtrend.label.1": "LL",
    "lex.downtrend.label.2": "LH",
    "lex.downtrend.label.3": "LL",
    "lex.bos.name": "BOS – Break of Structure",
    "lex.bos.tag": "Strukturbruch",
    "lex.bos.desc": "Schluss jenseits eines vorigen Swing-Hochs (Tiefs): der Trend läuft weiter.",
    "lex.bos.conf": "Kerzen-Schluss, nicht nur Docht; der Rücklauf hält über dem Level.",
    "lex.bos.inv": "Nur ein Docht durch und Schluss zurück: eher ein Sweep.",
    "lex.bos.label.0": "BOS",
    "lex.bos.level.0": "vorheriges Swing-Hoch",
    "lex.choch.name": "CHoCH – Change of Character",
    "lex.choch.tag": "Erster möglicher Trendwechsel",
    "lex.choch.desc": "Erster Bruch gegen die bisherige Struktur, z. B. ein Lower High im Abwärtstrend: Frühwarnung.",
    "lex.choch.conf": "Danach ein Higher Low, das hält, und ein BOS in die neue Richtung.",
    "lex.choch.inv": "Neues Tief unter dem Tief vor dem CHoCH.",
    "lex.choch.label.0": "CHoCH",
    "lex.choch.level.0": "letztes Lower High",
    "lex.mss.name": "MSS – Market Structure Shift",
    "lex.mss.tag": "Strukturwechsel mit Displacement",
    "lex.mss.desc": "Bruch des letzten relevanten Swings mit Displacement, oft direkt nach einem Sweep.",
    "lex.mss.conf": "Großer Schluss unter dem letzten Higher Low mit neuem FVG.",
    "lex.mss.inv": "Schluss wieder über dem Sweep-Hoch.",
    "lex.mss.label.0": "Sweep",
    "lex.mss.label.1": "MSS",
    "lex.mss.level.0": "altes Hoch (BSL)",
    "lex.mss.level.1": "letztes Higher Low",
    "lex.internalExternal.name": "Interne vs. externe Struktur",
    "lex.internalExternal.tag": "Welcher Swing zählt?",
    "lex.internalExternal.desc": "Externe Swings begrenzen die Range, interne liegen darin. Ein interner Bruch ist noch kein Trendwechsel.",
    "lex.internalExternal.conf": "Erst ein Schluss jenseits der externen Struktur bestätigt den Trend.",
    "lex.internalExternal.inv": "Interner CHoCH ohne externen Folgebruch: oft nur ein Rücklauf.",
    "lex.internalExternal.label.0": "interne Swings",
    "lex.internalExternal.level.0": "externes Hoch",
    "lex.internalExternal.level.1": "externes Tief",
    "lex.doubleTopBottom.name": "Double Top / Double Bottom",
    "lex.doubleTopBottom.tag": "Klassische Umkehrformation",
    "lex.doubleTopBottom.desc": "Zwei Hochs auf ähnlichem Niveau mit Nackenlinie dazwischen, zugleich ein Liquiditätspool.",
    "lex.doubleTopBottom.conf": "Schluss unter der Nackenlinie, Retest als Einstieg.",
    "lex.doubleTopBottom.inv": "Schluss über den Tops: Sweep oder Ausbruch.",
    "lex.doubleTopBottom.label.0": "Top 1",
    "lex.doubleTopBottom.label.1": "Top 2",
    "lex.doubleTopBottom.level.0": "Nackenlinie",
    "lex.range.name": "Range / Konsolidierung",
    "lex.range.tag": "Seitwärtsmarkt",
    "lex.range.desc": "Preis pendelt zwischen Widerstand und Unterstützung.",
    "lex.range.conf": "Rejection an der Kante und Schluss zurück in die Range.",
    "lex.range.inv": "Mehrere Schlüsse außerhalb: die Range ist vorbei.",
    "lex.range.level.0": "Resistance",
    "lex.range.level.1": "Support",
    "lex.buySellLiquidity.name": "BSL / SSL – Buy-side & Sell-side Liquidity",
    "lex.buySellLiquidity.tag": "Liquidität über/unter Swings",
    "lex.buySellLiquidity.desc": "Über Hochs liegt Buy-side, unter Tiefs Sell-side Liquidität: dort sammeln sich Stops.",
    "lex.buySellLiquidity.conf": "Nach dem Abholen: Schluss zurück und Strukturbruch in Gegenrichtung.",
    "lex.buySellLiquidity.inv": "Preis hält jenseits des Levels: echter Ausbruch.",
    "lex.buySellLiquidity.level.0": "BSL – über Hochs",
    "lex.buySellLiquidity.level.1": "SSL – unter Tiefs",
    "lex.equalHighsLows.name": "EQH / EQL – Equal Highs / Lows",
    "lex.equalHighsLows.tag": "Gleiche Hochs / Tiefs",
    "lex.equalHighsLows.desc": "Gleiche Hochs oder Tiefs bündeln sichtbare Liquidität, kein automatischer Widerstand.",
    "lex.equalHighsLows.conf": "Sweep mit Schluss zurück und LTF-MSS in Gegenrichtung.",
    "lex.equalHighsLows.inv": "Saubere Schlüsse jenseits und Retest hält: Ausbruch.",
    "lex.equalHighsLows.level.0": "EQH",
    "lex.equalHighsLows.level.1": "EQL",
    "lex.sweep.name": "Liquidity Sweep",
    "lex.sweep.tag": "Abholen von Stops + Rückkehr",
    "lex.sweep.desc": "Kurzer Stich über ein Hoch (unter ein Tief), dann Rückkehr: Stops abgeholt.",
    "lex.sweep.conf": "Schluss zurück auf der alten Seite, dann Displacement und MSS.",
    "lex.sweep.inv": "Folgekerzen schließen jenseits und halten: Ausbruch.",
    "lex.sweep.label.0": "Sweep",
    "lex.sweep.label.1": "Close zurück",
    "lex.sweep.level.0": "Swing-High / Liquidität",
    "lex.sessionLiquidity.name": "Session-Liquidität (PDH/PDL, Asia-Range)",
    "lex.sessionLiquidity.tag": "Zeitbasierte Liquiditätsziele",
    "lex.sessionLiquidity.desc": "Vortages-, Wochen- und Session-Hochs/-Tiefe werden oft zur London- oder NY-Eröffnung abgeholt.",
    "lex.sessionLiquidity.conf": "Sweep des Levels, Schluss zurück in die Range, LTF-MSS in einer Killzone.",
    "lex.sessionLiquidity.inv": "Preis hält außerhalb der Asia-Range: Trendtag.",
    "lex.sessionLiquidity.label.0": "London sweept Asia High",
    "lex.sessionLiquidity.level.0": "Asia High",
    "lex.sessionLiquidity.level.1": "Asia Low",
    "lex.fvgBull.name": "FVG – Fair Value Gap (bullisch)",
    "lex.fvgBull.tag": "Dreikerzen-Ungleichgewicht",
    "lex.fvgBull.desc": "Hoch von Kerze 1 unter dem Tief von Kerze 3: eine Lücke, in die der Preis zurücklaufen kann.",
    "lex.fvgBull.conf": "Schlüsse bleiben über dem unteren Rand, Reaktionskerze schließt nach oben heraus.",
    "lex.fvgBull.inv": "Schluss unter dem unteren Rand: FVG gescheitert.",
    "lex.fvgBull.label.0": "Kerze 3",
    "lex.fvgBull.zone.0": "Bullish FVG: High K1 bis Low K3",
    "lex.fvgBear.name": "FVG – Fair Value Gap (bärisch)",
    "lex.fvgBear.tag": "Dreikerzen-Ungleichgewicht",
    "lex.fvgBear.desc": "Tief von Kerze 1 über dem Hoch von Kerze 3: ein Ungleichgewicht nach unten.",
    "lex.fvgBear.conf": "Schlüsse bleiben unter dem oberen Rand, Reaktionskerze schließt nach unten heraus.",
    "lex.fvgBear.inv": "Schluss über dem oberen Rand: FVG gescheitert.",
    "lex.fvgBear.zone.0": "Bearish FVG: Low K1 bis High K3",
    "lex.ifvg.name": "iFVG – Inverse FVG",
    "lex.ifvg.tag": "Rollenwechsel einer FVG",
    "lex.ifvg.desc": "Ein durchbrochenes FVG wechselt die Rolle: aus Unterstützung wird Widerstand (und umgekehrt).",
    "lex.ifvg.conf": "Schluss durch das ganze FVG, beim Retest Ablehnung.",
    "lex.ifvg.inv": "Schluss zurück durch die Zone.",
    "lex.ifvg.label.0": "Durchbruch",
    "lex.ifvg.label.1": "Retest",
    "lex.ifvg.zone.0": "ehemalige bullische FVG",
    "lex.orderBlock.name": "OB – Order Block",
    "lex.orderBlock.tag": "Letzte Gegenkerze vor Impuls",
    "lex.orderBlock.desc": "Letzte Gegenkerze vor einem Impuls, der Struktur bricht.",
    "lex.orderBlock.conf": "Impuls bricht Struktur (BOS), beim Rücklauf Reaktion mit LTF-Bestätigung.",
    "lex.orderBlock.inv": "Schluss jenseits des OB: ungültig, evtl. Breaker.",
    "lex.orderBlock.label.0": "letzte rote Kerze",
    "lex.orderBlock.zone.0": "möglicher bullish OB",
    "lex.breaker.name": "Breaker Block",
    "lex.breaker.tag": "Gescheiterter OB mit Rollenwechsel",
    "lex.breaker.desc": "Ein nach einem Sweep durchbrochener Order Block wirkt danach von der Gegenseite.",
    "lex.breaker.conf": "Bruch mit Schluss und Displacement, der Retest wird abgelehnt.",
    "lex.breaker.inv": "Schluss zurück durch die Zone.",
    "lex.breaker.label.0": "Sweep",
    "lex.breaker.label.1": "Bruch",
    "lex.breaker.label.2": "Rücktest",
    "lex.breaker.zone.0": "ehemaliger bullish OB → Widerstand",
    "lex.mitigation.name": "Mitigation Block",
    "lex.mitigation.tag": "Gescheiterter OB ohne Sweep",
    "lex.mitigation.desc": "Wie ein Breaker, aber ohne Sweep: erst ein Lower High, dann bricht das Tief.",
    "lex.mitigation.conf": "Beim Rücktest Rejection und Schluss zurück unter die Zone.",
    "lex.mitigation.inv": "Schluss über der Zone bzw. dem Lower High.",
    "lex.mitigation.label.0": "Lower High",
    "lex.mitigation.label.1": "Rücktest",
    "lex.mitigation.zone.0": "Mitigation Block",
    "lex.poi.name": "POI – Point of Interest",
    "lex.poi.tag": "Interessanter Preisbereich",
    "lex.poi.desc": "Zone, an der eine Reaktion gesucht wird: OB, FVG, Swing-Level, Supply/Demand, Liquidität. Noch kein Signal.",
    "lex.poi.conf": "Rejection-Kerze, Sweep in der Zone oder LTF-CHoCH.",
    "lex.poi.inv": "Durchlaufen ohne Reaktion, Schluss dahinter.",
    "lex.poi.zone.0": "POI-Zone",
    "lex.supplyDemand.name": "Supply & Demand",
    "lex.supplyDemand.tag": "Angebots- und Nachfragezone",
    "lex.supplyDemand.desc": "Bereiche, von denen deutlicher Verkaufs- (Supply) bzw. Kaufdruck (Demand) ausging.",
    "lex.supplyDemand.conf": "Frische Zone, beim ersten Test Rejection und LTF-Strukturwechsel.",
    "lex.supplyDemand.inv": "Schluss durch die ganze Zone; jeder Test schwächt sie.",
    "lex.supplyDemand.zone.0": "Supply",
    "lex.supplyDemand.zone.1": "Demand",
    "lex.displacement.name": "Displacement",
    "lex.displacement.tag": "Impulsive Preisbewegung",
    "lex.displacement.desc": "Schnelle Bewegung mit großen Kerzen und wenig Überlappung, hinterlässt oft ein FVG.",
    "lex.displacement.conf": "Große Körper, kleine Dochte, bricht ein Swing-Level.",
    "lex.displacement.inv": "Die Bewegung wird sofort ganz zurückgenommen.",
    "lex.inducement.name": "Inducement (IDM)",
    "lex.inducement.tag": "Möglicher Liquiditätsköder",
    "lex.inducement.desc": "Kleiner Zwischen-Swing als Köder: der Preis nimmt ihn, bevor er das größere Ziel anläuft.",
    "lex.inducement.conf": "Erst nach dem Abholen des IDM reagiert der Preis am eigentlichen POI.",
    "lex.inducement.inv": "Wer direkt am IDM einsteigt, wird oft selbst Liquidität.",
    "lex.inducement.label.0": "IDM",
    "lex.inducement.label.1": "IDM genommen",
    "lex.inducement.label.2": "größeres Ziel",
    "lex.ote.name": "OTE – Optimal Trade Entry",
    "lex.ote.tag": "Fibonacci-Retracement-Bereich",
    "lex.ote.desc": "Rücklauf von etwa 62–79 % eines Impulses, der vorher Struktur gebrochen hat.",
    "lex.ote.conf": "In der OTE folgt Rejection oder LTF-MSS.",
    "lex.ote.inv": "Schluss jenseits von 100 %.",
    "lex.ote.level.0": "ca. 62 %",
    "lex.ote.level.1": "ca. 79 %",
    "lex.ladder.name": "Die Bestätigungs-Leiter",
    "lex.ladder.tag": "Von „Zone erreicht“ bis „bestätigt“",
    "lex.ladder.desc": "Bestätigung ist eine Abfolge. Je höher die Stufe, desto sicherer, aber desto schlechter Einstieg und CRV.",
    "lex.ladder.list.0.title": "Stufe 0 – Zone erreicht:",
    "lex.ladder.list.0": "Preis ist am POI (OB, FVG, Supply/Demand, Liquidität). Noch kein Signal.",
    "lex.ladder.list.1.title": "Stufe 1 – Reaktion:",
    "lex.ladder.list.1": "Docht/Rejection, Verlangsamung oder Sweep innerhalb der Zone.",
    "lex.ladder.list.2.title": "Stufe 2 – Close-Bestätigung:",
    "lex.ladder.list.2": "Eine Kerze schließt auf der richtigen Seite (z. B. über dem Hammer-High, zurück in die Range, über dem Sweep-Level).",
    "lex.ladder.list.3.title": "Stufe 3 – Strukturbestätigung:",
    "lex.ladder.list.3": "LTF-CHoCH/MSS mit Displacement, idealerweise mit neuem FVG.",
    "lex.ladder.list.4.title": "Stufe 4 – Follow-through:",
    "lex.ladder.list.4": "Retest des gebrochenen Levels/FVG hält und der nächste Swing läuft in die neue Richtung.",
    "lex.wickClose.name": "Docht vs. Close",
    "lex.wickClose.tag": "Wann ist ein Level wirklich gebrochen?",
    "lex.wickClose.desc": "Ein Level ist erst gebrochen, wenn der Körper jenseits schließt; ein Docht ist nur eine Probe.",
    "lex.wickClose.conf": "Schluss jenseits; strenger: auch die Folgekerze oder der Retest hält.",
    "lex.wickClose.inv": "Schluss zurück binnen weniger Kerzen.",
    "lex.wickClose.label.0": "nur Docht",
    "lex.wickClose.label.1": "Close darüber",
    "lex.wickClose.level.0": "Level",
    "lex.signalCandle.name": "Signal- und Bestätigungskerze",
    "lex.signalCandle.tag": "Follow-through abwarten",
    "lex.signalCandle.desc": "Das Signal entsteht an der Zone, die nächste Kerze bestätigt es.",
    "lex.signalCandle.conf": "Schluss über dem Signal-Hoch, Stop unter dem Signal-Tief.",
    "lex.signalCandle.inv": "Bestätigungskerze schließt unter dem Signal-Tief.",
    "lex.signalCandle.label.0": "Signal",
    "lex.signalCandle.label.1": "Bestätigung",
    "lex.signalCandle.level.0": "Signal-High",
    "lex.breakRetest.name": "Break & Retest",
    "lex.breakRetest.tag": "Rollenwechsel bestätigen",
    "lex.breakRetest.desc": "Nach dem Ausbruch kehrt der Preis zum Level zurück; hält es von der anderen Seite, ist der Ausbruch bestätigt.",
    "lex.breakRetest.conf": "Retest wird mit Docht abgelehnt, Schlüsse bleiben über dem Level.",
    "lex.breakRetest.inv": "Schluss zurück unter das Level: Fakeout.",
    "lex.breakRetest.label.0": "Break (Close)",
    "lex.breakRetest.label.1": "Retest hält",
    "lex.breakRetest.level.0": "altes Hoch → neuer Support",
    "lex.sweepMssFvg.name": "Entry-Modell: Sweep → MSS → FVG",
    "lex.sweepMssFvg.tag": "Typische SMC-Bestätigungskette",
    "lex.sweepMssFvg.desc": "Sweep, dann bricht ein Displacement den letzten Swing (MSS) mit FVG, Einstieg beim Rücklauf ins FVG.",
    "lex.sweepMssFvg.conf": "Alle vier Schritte in dieser Reihenfolge, in Richtung des HTF-Bias.",
    "lex.sweepMssFvg.inv": "Schluss unter dem Sweep-Tief bzw. dem FVG.",
    "lex.sweepMssFvg.label.0": "1 Sweep",
    "lex.sweepMssFvg.label.1": "2 MSS",
    "lex.sweepMssFvg.label.2": "3 Retest",
    "lex.sweepMssFvg.label.3": "4 Entry",
    "lex.sweepMssFvg.level.0": "SSL (altes Tief)",
    "lex.sweepMssFvg.level.1": "MSS-Level",
    "lex.sweepMssFvg.zone.0": "FVG",
    "lex.ltfInHtfPoi.name": "LTF-Bestätigung im HTF-POI",
    "lex.ltfInHtfPoi.tag": "HTF = wo, LTF = wann",
    "lex.ltfInHtfPoi.desc": "Der höhere Timeframe bestimmt, wo gehandelt wird, der niedrigere, wann.",
    "lex.ltfInHtfPoi.conf": "LTF-Schluss über dem letzten Lower High, Einstieg beim Retest.",
    "lex.ltfInHtfPoi.inv": "LTF-Schluss unter dem HTF-POI.",
    "lex.ltfInHtfPoi.label.0": "LTF-CHoCH",
    "lex.ltfInHtfPoi.level.0": "letztes LTF Lower High",
    "lex.ltfInHtfPoi.zone.0": "HTF-POI (z. B. 4H Demand)",
    "lex.volume.name": "Volumen-Bestätigung",
    "lex.volume.tag": "Teilnahme hinter der Bewegung",
    "lex.volume.desc": "Echte Ausbrüche kommen oft mit hohem Volumen; im CFD gibt es nur Tick-Volumen.",
    "lex.volume.conf": "Ausbruchskerze mit deutlich mehr Volumen, Rücklauf mit weniger.",
    "lex.volume.inv": "Ausbruch mit schwachem Volumen und sofortiger Rückkehr.",
    "lex.volume.label.0": "hohes Volumen",
    "lex.volume.level.0": "Resistance",
    "lex.fakeout.name": "Fakeout – fehlende Bestätigung",
    "lex.fakeout.tag": "Typische Fehlsignale",
    "lex.fakeout.desc": "Nur ein Docht oder ein knapper Schluss über dem Level ohne Folgekerze; die Rückkehr ist ein Signal in Gegenrichtung.",
    "lex.fakeout.conf": "Eine Kerze schließt wieder klar auf der alten Seite.",
    "lex.fakeout.inv": "Die Folgekerze läuft jenseits weiter: echter Ausbruch.",
    "lex.fakeout.label.0": "schwacher Ausbruch",
    "lex.fakeout.label.1": "Close zurück",
    "lex.fakeout.level.0": "Resistance",
    "lex.checklist.name": "Bestätigungs-Checkliste vor dem Entry",
    "lex.checklist.tag": "Alles abhaken, dann handeln",
    "lex.checklist.desc": "Vorher festlegen, welche Punkte Pflicht sind.",
    "lex.checklist.list.0": "HTF-Bias klar? (Struktur auf 4H/1H, letzter relevanter Swing hält)",
    "lex.checklist.list.1": "Preis an einem HTF-POI bzw. im Discount (Long) oder Premium (Short)?",
    "lex.checklist.list.2": "Liquidität vor der Reaktion abgeholt (Sweep)?",
    "lex.checklist.list.3": "Reaktionskerze mit Close auf der richtigen Seite?",
    "lex.checklist.list.4": "LTF-Strukturbruch (CHoCH/MSS) mit Displacement?",
    "lex.checklist.list.5": "FVG/OB als Entry-Zone entstanden?",
    "lex.checklist.list.6": "Invalidation und SL klar definiert, CRV ≥ geplantes Minimum?",
    "lex.checklist.list.7": "Keine High-Impact-News in Kürze, Spread normal, in einer aktiven Session?",
    "lex.premiumDiscount.name": "Premium / Discount / Equilibrium",
    "lex.premiumDiscount.tag": "Range in Hälften teilen",
    "lex.premiumDiscount.desc": "Über der Mitte einer Swing-Range ist Premium, darunter Discount.",
    "lex.premiumDiscount.conf": "Long im Discount, Short im Premium, jeweils mit Reaktion und Struktur.",
    "lex.premiumDiscount.inv": "Range gebrochen: neue Range ziehen.",
    "lex.premiumDiscount.level.0": "50 % – Equilibrium",
    "lex.premiumDiscount.zone.0": "Premium",
    "lex.premiumDiscount.zone.1": "Discount",
    "lex.fibRetracement.name": "Fibonacci Retracement",
    "lex.fibRetracement.tag": "Rücklauf eines Swings",
    "lex.fibRetracement.desc": "Wie weit der Preis einen Impuls zurücknimmt: 38,2, 50, 61,8, 70,5, 78,6 %.",
    "lex.fibRetracement.conf": "Reaktion am Level mit Überlappung von OB/FVG/Struktur.",
    "lex.fibRetracement.inv": "Schluss jenseits von 100 %.",
    "lex.fibRetracement.level.0": "23,6 %",
    "lex.fibRetracement.level.1": "38,2 %",
    "lex.fibRetracement.level.2": "50,0 %",
    "lex.fibRetracement.level.3": "61,8 %",
    "lex.fibRetracement.level.4": "78,6 %",
    "lex.fibExtension.name": "Fibonacci Extension",
    "lex.fibExtension.tag": "Mögliche Projektionsziele",
    "lex.fibExtension.desc": "Kursziele über den Swing hinaus, oft 127,2 % und 161,8 %.",
    "lex.fibExtension.level.0": "161,8 %",
    "lex.fibExtension.level.1": "127,2 %",
    "lex.fibExtension.level.2": "100 %",
    "lex.atr.name": "ATR – Average True Range",
    "lex.atr.tag": "Volatilität, keine Richtung",
    "lex.atr.desc": "Durchschnittliche Schwankung je Kerze: zeigt die Stärke der Bewegung, nicht die Richtung.",
    "lex.stopTarget.name": "Stop Loss / Take Profit",
    "lex.stopTarget.tag": "Risiko und Ziel vorab festlegen",
    "lex.stopTarget.desc": "Stop an der Invalidierung, Ziel am geplanten Level, die Größe passend zum Risiko.",
    "lex.stopTarget.level.0": "Take Profit",
    "lex.stopTarget.level.1": "Entry",
    "lex.stopTarget.level.2": "Stop Loss",
    "lex.positionSize.name": "Positionsgröße berechnen",
    "lex.positionSize.tag": "Risiko in Geld → Lots",
    "lex.positionSize.desc": "Positionsgröße = Risikobetrag ÷ (Stop-Abstand × Wert je Punkt).",
    "lex.positionSize.list.0": "Konto 10.000 $, Risiko 1 % → 100 $",
    "lex.positionSize.list.1": "EUR/USD: Entry 1,1000, SL 1,0980 → 20 Pips",
    "lex.positionSize.list.2": "Pip-Wert ca. 10 $ pro Standard-Lot (bei EUR-Konto umrechnen)",
    "lex.positionSize.list.3": "100 $ ÷ (20 × 10 $) = 0,5 Lot",
    "lex.positionSize.list.4": "Gebühren/Spread einrechnen; bei Indizes/Gold den Punktwert des Brokers prüfen.",
    "lex.rewardRisk.name": "CRV & nötige Trefferquote",
    "lex.rewardRisk.tag": "Risk-Reward richtig einordnen",
    "lex.rewardRisk.desc": "Nötige Trefferquote ohne Kosten = 1 ÷ (1 + CRV).",
    "lex.rewardRisk.list.0": "CRV 1 : 1 → 50 % Trefferquote nötig",
    "lex.rewardRisk.list.1": "CRV 1 : 2 → 33,3 %",
    "lex.rewardRisk.list.2": "CRV 1 : 3 → 25 %",
    "lex.rewardRisk.list.3": "CRV 1 : 5 → 16,7 %",
    "lex.rMultiple.name": "R-Multiple & Break-even",
    "lex.rMultiple.tag": "Ergebnis in Risikoeinheiten",
    "lex.rMultiple.desc": "1R ist das geplante Risiko; Einstand verschiebt den Stop zum Einstieg, Kosten bleiben.",
    "lex.rMultiple.level.0": "0R / Entry",
    "lex.rMultiple.level.1": "−1R",
    "lex.rMultiple.level.2": "+2R",
    "lex.topDown.name": "HTF / LTF – Top-down",
    "lex.topDown.tag": "Von groß nach klein analysieren",
    "lex.topDown.desc": "Der höhere Timeframe gibt den Kontext, der niedrigere Auslöser und Ausführung.",
    "lex.topDown.conf": "LTF-Signal nur in Richtung des HTF-Bias oder an einem HTF-POI.",
    "lex.topDown.inv": "LTF-Signal gegen den HTF ohne POI: geringe Qualität.",
    "lex.topDown.label.0": "HTF-Kontext",
    "lex.topDown.label.1": "LTF-Details",
    "lex.bias.name": "Market Bias",
    "lex.bias.tag": "Bevorzugte Marktrichtung",
    "lex.bias.desc": "Arbeitshypothese bullisch, bärisch oder neutral aus Struktur und Kontext, regelmäßig neu bewerten.",
    "lex.bias.conf": "Gilt, solange der letzte relevante HTF-Swing hält.",
    "lex.bias.inv": "Schluss jenseits dieses Swings: neutral, neu bewerten.",
    "lex.bias.label.0": "bullischer Bias",
    "lex.killzones.name": "Sessions & Killzones",
    "lex.killzones.tag": "Wann bewegt sich der Markt?",
    "lex.killzones.desc": "Die meisten Sweeps und Displacements passieren zu Session-Eröffnungen.",
    "lex.killzones.list.0": "Asia-Range: ca. 01:00–08:00 MEZ – bildet oft das Hoch/Tief, das später abgeholt wird",
    "lex.killzones.list.1": "London Killzone: ca. 08:00–11:00 MEZ",
    "lex.killzones.list.2": "New York AM Killzone: ca. 13:00–16:00 MEZ (US-Daten oft 14:30, Aktienmarkt-Eröffnung 15:30)",
    "lex.killzones.list.3": "Zeiten nach ICT-Definition in New-York-Zeit – bei der Zeitumstellung (USA und EU nicht gleichzeitig) verschieben sie sich um eine Stunde.",
    "lex.news.name": "News & High-Impact-Events",
    "lex.news.tag": "Wenn Charts nicht mehr zählen",
    "lex.news.desc": "Zinsentscheide, NFP, CPI können Levels in Sekunden durchschlagen und den Spread aufblähen. Kein neuer Trade 15–30 Min. davor und danach.",
    "lex.confluence.name": "Confluence",
    "lex.confluence.tag": "Mehrere Hinweise treffen zusammen",
    "lex.confluence.desc": "Mehrere unabhängige Hinweise heben die Qualität, garantieren aber nichts.",
    "lex.confluence.conf": "Unabhängige Faktoren: Zeitebene, Zone, Liquidität, Struktur, Zeit. Drei Fib-Level sind keine drei Gründe.",
    "lex.confluence.label.0": "mehrere Faktoren",
    "lex.invalidation.name": "Invalidation",
    "lex.invalidation.tag": "Setup-Idee ist nicht mehr gültig",
    "lex.invalidation.desc": "Vorher festlegen, welcher Preis die Idee widerlegt, und nicht nachträglich verschieben.",
    "lex.invalidation.level.0": "Invalidierungslevel",
    "lex.spread.name": "Spread & Slippage",
    "lex.spread.tag": "Ausführungskosten",
    "lex.spread.desc": "Spread = Ask − Bid, Slippage = Abweichung beim Fill; beide steigen bei News und dünnem Markt.",
    "lex.spread.level.0": "Ask",
    "lex.spread.level.1": "Bid",
    "lex.journal.name": "Backtesting & Journal",
    "lex.journal.tag": "Regeln messen statt glauben",
    "lex.journal.desc": "Ein Setup ist nur so gut wie seine geprüften Regeln; erst nach 50–100 Trades urteilen.",
    "lex.cat.candles": "Candlestick-Muster",
    "lex.cat.structure": "Marktstruktur",
    "lex.cat.smc": "Liquidity & SMC",
    "lex.cat.confirmation": "Bestätigung erkennen",
    "lex.cat.risk": "Fibonacci & Risiko",
    "lex.cat.context": "Kontext & Ausführung",
    "lex.cat.patterns": "Chartformationen",
    "lex.cat.trend": "Trend & Levels",
    "lex.cat.indicators": "Indikatoren",
    "lex.cat.phases": "Marktphasen & Methoden",
    "lex.cat.psychology": "Psychologie",
    "lex.headShoulders.name": "Head & Shoulders",
    "lex.headShoulders.tag": "Umkehr nach einem Aufwärtstrend",
    "lex.headShoulders.desc": "Drei Hochs, das mittlere am höchsten, die Tiefs dazwischen bilden die Nackenlinie.",
    "lex.headShoulders.conf": "Schluss unter der Nackenlinie; ein Retest von unten kann der Einstieg sein.",
    "lex.headShoulders.inv": "Schluss über der rechten Schulter.",
    "lex.headShoulders.label.0": "Schulter",
    "lex.headShoulders.label.1": "Kopf",
    "lex.headShoulders.label.2": "Schulter",
    "lex.headShoulders.level.0": "Nackenlinie",
    "lex.inverseHeadShoulders.name": "Umgekehrte Head & Shoulders",
    "lex.inverseHeadShoulders.tag": "Umkehr nach einem Abwärtstrend",
    "lex.inverseHeadShoulders.desc": "Drei Tiefs, das mittlere am tiefsten, die Hochs dazwischen bilden die Nackenlinie.",
    "lex.inverseHeadShoulders.conf": "Schluss über der Nackenlinie; ein Retest von oben kann der Einstieg sein.",
    "lex.inverseHeadShoulders.inv": "Schluss unter der rechten Schulter.",
    "lex.inverseHeadShoulders.label.0": "Schulter",
    "lex.inverseHeadShoulders.label.1": "Kopf",
    "lex.inverseHeadShoulders.label.2": "Schulter",
    "lex.inverseHeadShoulders.level.0": "Nackenlinie",
    "lex.tripleTopBottom.name": "Triple Top / Bottom",
    "lex.tripleTopBottom.tag": "Dreimal abgewiesen",
    "lex.tripleTopBottom.desc": "Drei Hochs (Tiefs) auf gleicher Höhe: das Level wurde dreimal verteidigt. Hier gezeigt: Triple Top.",
    "lex.tripleTopBottom.conf": "Schluss unter der Nackenlinie (Tiefs dazwischen).",
    "lex.tripleTopBottom.inv": "Schluss über den Tops.",
    "lex.tripleTopBottom.label.0": "1",
    "lex.tripleTopBottom.label.1": "2",
    "lex.tripleTopBottom.label.2": "3",
    "lex.tripleTopBottom.level.0": "Nackenlinie",
    "lex.ascendingTriangle.name": "Aufsteigendes Dreieck",
    "lex.ascendingTriangle.tag": "Steigende Tiefs gegen flaches Hoch",
    "lex.ascendingTriangle.desc": "Flacher Widerstand, steigende Tiefs: Käufer werden ungeduldiger, meist Ausbruch nach oben.",
    "lex.ascendingTriangle.conf": "Schluss über dem Widerstand, besser mit Retest.",
    "lex.ascendingTriangle.inv": "Schluss unter der Linie der steigenden Tiefs.",
    "lex.ascendingTriangle.label.0": "Ausbruch",
    "lex.ascendingTriangle.level.0": "Widerstand",
    "lex.descendingTriangle.name": "Absteigendes Dreieck",
    "lex.descendingTriangle.tag": "Fallende Hochs gegen flaches Tief",
    "lex.descendingTriangle.desc": "Flache Unterstützung, fallende Hochs: Verkäufer drücken, meist Ausbruch nach unten.",
    "lex.descendingTriangle.conf": "Schluss unter der Unterstützung.",
    "lex.descendingTriangle.inv": "Schluss über der Linie der fallenden Hochs.",
    "lex.descendingTriangle.label.0": "Ausbruch",
    "lex.descendingTriangle.level.0": "Unterstützung",
    "lex.symmetricalTriangle.name": "Symmetrisches Dreieck",
    "lex.symmetricalTriangle.tag": "Kompression, Richtung offen",
    "lex.symmetricalTriangle.desc": "Fallende Hochs und steigende Tiefs laufen zusammen; der Ausbruch geht meist in Trendrichtung.",
    "lex.symmetricalTriangle.conf": "Schluss außerhalb des Dreiecks, nicht nur ein Docht.",
    "lex.symmetricalTriangle.inv": "Rückkehr ins Dreieck nach dem Ausbruch: Fakeout.",
    "lex.symmetricalTriangle.label.0": "Ausbruch",
    "lex.flag.name": "Flagge",
    "lex.flag.tag": "Kurze Pause im Trend",
    "lex.flag.desc": "Nach einem steilen Anstieg (Fahnenstange) ein kleiner, gegenläufiger Kanal; danach läuft der Trend meist weiter.",
    "lex.flag.conf": "Schluss über der oberen Flaggenlinie.",
    "lex.flag.inv": "Rücklauf tiefer als die halbe Fahnenstange.",
    "lex.flag.label.0": "Fahnenstange",
    "lex.flag.label.1": "Flagge",
    "lex.pennant.name": "Wimpel",
    "lex.pennant.tag": "Kleines Dreieck nach dem Impuls",
    "lex.pennant.desc": "Wie die Flagge, aber die Konsolidierung läuft als kleines symmetrisches Dreieck zusammen.",
    "lex.pennant.conf": "Schluss aus dem Wimpel in Richtung des Impulses.",
    "lex.pennant.inv": "Ausbruch gegen den Impuls.",
    "lex.pennant.label.0": "Wimpel",
    "lex.risingWedge.name": "Steigender Keil",
    "lex.risingWedge.tag": "Ermüdender Anstieg",
    "lex.risingWedge.desc": "Höhere Hochs und Tiefs, aber die Spanne wird enger: der Anstieg verliert Kraft, oft folgt ein Bruch nach unten.",
    "lex.risingWedge.conf": "Schluss unter der unteren Keillinie.",
    "lex.risingWedge.inv": "Schluss über der oberen Keillinie.",
    "lex.fallingWedge.name": "Fallender Keil",
    "lex.fallingWedge.tag": "Ermüdender Fall",
    "lex.fallingWedge.desc": "Tiefere Hochs und Tiefs, die Spanne wird enger: der Fall verliert Kraft, oft folgt ein Bruch nach oben.",
    "lex.fallingWedge.conf": "Schluss über der oberen Keillinie.",
    "lex.fallingWedge.inv": "Schluss unter der unteren Keillinie.",
    "lex.trendChannel.name": "Trendkanal",
    "lex.trendChannel.tag": "Trend zwischen zwei Parallelen",
    "lex.trendChannel.desc": "Hochs und Tiefs laufen zwischen zwei parallelen Linien; an der unteren wird im Aufwärtskanal gekauft.",
    "lex.trendChannel.conf": "Reaktion an der Kanallinie mit Schluss zurück in den Kanal.",
    "lex.trendChannel.inv": "Schluss außerhalb des Kanals gegen den Trend.",
    "lex.trendChannel.label.0": "Kanal",
    "lex.cupHandle.name": "Cup & Handle",
    "lex.cupHandle.tag": "Runde Basis, dann Ausbruch",
    "lex.cupHandle.desc": "Eine runde Basis (Tasse), danach ein kleiner Rücklauf (Henkel) unter dem Rand; der Ausbruch über den Rand setzt den Anstieg fort.",
    "lex.cupHandle.conf": "Schluss über dem Rand der Tasse.",
    "lex.cupHandle.inv": "Henkel tiefer als die halbe Tasse.",
    "lex.cupHandle.label.0": "Tasse",
    "lex.cupHandle.label.1": "Henkel",
    "lex.cupHandle.level.0": "Rand",
    "lex.trendline.name": "Trendlinie",
    "lex.trendline.tag": "Linie durch die Tiefs (Hochs)",
    "lex.trendline.desc": "Im Aufwärtstrend durch mindestens zwei steigende Tiefs gezogen; ab dem dritten Berührungspunkt gilt sie als bestätigt.",
    "lex.trendline.conf": "Reaktion an der Linie, Schluss bleibt darüber.",
    "lex.trendline.inv": "Schluss unter der Trendlinie.",
    "lex.trendline.label.0": "1",
    "lex.trendline.label.1": "2",
    "lex.trendline.label.2": "3",
    "lex.movingAverages.name": "Gleitende Durchschnitte",
    "lex.movingAverages.tag": "EMA 20 / 50 / 200",
    "lex.movingAverages.desc": "Der Durchschnittskurs der letzten n Kerzen: über einem steigenden Durchschnitt ist der Trend aufwärts, er dient oft als dynamische Unterstützung.",
    "lex.movingAverages.conf": "Rücklauf zum Durchschnitt hält, Schluss wieder darüber.",
    "lex.movingAverages.inv": "Schluss unter dem Durchschnitt und er dreht nach unten.",
    "lex.goldenDeathCross.name": "Golden / Death Cross",
    "lex.goldenDeathCross.tag": "Kreuzung zweier Durchschnitte",
    "lex.goldenDeathCross.desc": "Golden Cross: der schnelle Durchschnitt (z. B. 50) kreuzt den langsamen (200) nach oben; Death Cross nach unten. Ein spätes Trendsignal.",
    "lex.goldenDeathCross.conf": "Der Kurs bleibt auf der Seite der Kreuzung.",
    "lex.goldenDeathCross.inv": "Schnelle Rückkreuzung: Seitwärtsphase.",
    "lex.maRibbon.name": "Moving-Average-Ribbon",
    "lex.maRibbon.tag": "18 EMAs 5–90 als ein Band",
    "lex.maRibbon.desc": "Viele Durchschnitte wachsender Länge als ein Band (z. B. das Madrid-Ribbon: 18 EMAs von 5 bis 90 Kerzen): im starken Trend aufgefächert und parallel, die kurzen über den langen aufwärts, darunter abwärts; seitwärts eng und verdreht.",
    "lex.maRibbon.conf": "Das Band weitet sich in Trendrichtung, ein Rücklauf dreht vor den langen Durchschnitten.",
    "lex.maRibbon.inv": "Die kurzen Durchschnitte laufen durch die langen und das Band zieht sich zusammen: der Trend lässt nach oder dreht.",
    "lex.supportResistance.name": "Unterstützung / Widerstand",
    "lex.supportResistance.tag": "Wo Käufer bzw. Verkäufer warten",
    "lex.supportResistance.desc": "Preisbereiche, an denen der Kurs mehrfach gedreht hat: darunter Käufer (Unterstützung), darüber Verkäufer (Widerstand). Gebrochen wechseln sie die Rolle.",
    "lex.supportResistance.conf": "Reaktion am Level mit Schluss zurück.",
    "lex.supportResistance.inv": "Saubere Schlüsse jenseits des Levels.",
    "lex.supportResistance.level.0": "Widerstand",
    "lex.supportResistance.level.1": "Unterstützung",
    "lex.roundNumbers.name": "Runde Marken",
    "lex.roundNumbers.tag": "Psychologische Level",
    "lex.roundNumbers.desc": "Glatte Preise (z. B. 4.000) ziehen Orders und Stops an; der Kurs reagiert dort häufiger oder läuft nach dem Bruch weiter.",
    "lex.roundNumbers.conf": "Schluss durch die Marke (Ausbruch) oder Ablehnung mit Docht (Reaktion).",
    "lex.roundNumbers.inv": "Mehrfaches Pendeln um die Marke: kein Signal.",
    "lex.roundNumbers.level.0": "runde Marke",
    "lex.pivotPoints.name": "Pivot Points",
    "lex.pivotPoints.tag": "Tageslevels aus dem Vortag",
    "lex.pivotPoints.desc": "Pivot = (Hoch + Tief + Schluss) ÷ 3 des Vortags, dazu R1/S1 darüber und darunter: viele Intraday-Trader beachten diese Level.",
    "lex.pivotPoints.conf": "Reaktion an P, R1 oder S1.",
    "lex.pivotPoints.inv": "Der Kurs läuft ohne Reaktion durch.",
    "lex.pivotPoints.level.0": "R1",
    "lex.pivotPoints.level.1": "Pivot",
    "lex.pivotPoints.level.2": "S1",
    "lex.gaps.name": "Kurslücke (Gap)",
    "lex.gaps.tag": "Sprung zwischen zwei Kerzen",
    "lex.gaps.desc": "Der Kurs eröffnet deutlich über (unter) dem letzten Schluss, z. B. nach dem Wochenende; viele Gaps werden später wieder geschlossen.",
    "lex.gaps.conf": "Rücklauf in die Lücke und Reaktion an ihrem Rand.",
    "lex.gaps.inv": "Der Kurs läuft vom Gap weg weiter: Ausbruchs-Gap.",
    "lex.gaps.zone.0": "Gap",
    "lex.heikinAshi.name": "Heikin Ashi",
    "lex.heikinAshi.tag": "Geglättete Kerzen",
    "lex.heikinAshi.desc": "Kerzen aus Durchschnittswerten: Trends werden als Folgen gleichfarbiger Kerzen ohne Gegendocht sichtbar. Die Preise sind nicht die echten.",
    "lex.heikinAshi.conf": "Mehrere gleichfarbige Kerzen ohne Docht auf der Gegenseite.",
    "lex.heikinAshi.inv": "Kleine Körper mit Dochten auf beiden Seiten: der Trend lässt nach.",
    "lex.rsi.name": "RSI",
    "lex.rsi.tag": "Relative Stärke 0–100",
    "lex.rsi.desc": "Misst die Stärke der letzten Bewegungen (meist 14 Kerzen): über 70 überkauft, unter 30 überverkauft — im Trend kann das lange so bleiben.",
    "lex.rsi.conf": "Rückkehr aus dem Extrembereich zusammen mit einer Strukturreaktion.",
    "lex.rsi.inv": "Allein im Extrembereich: kein Umkehrsignal.",
    "lex.divergence.name": "Divergenz",
    "lex.divergence.tag": "Preis und Indikator laufen auseinander",
    "lex.divergence.desc": "Der Preis macht ein höheres Hoch, der Indikator (RSI, MACD) ein tieferes: der Anstieg verliert Kraft. Hier gezeigt: bärische Divergenz.",
    "lex.divergence.conf": "Strukturbruch in die Gegenrichtung nach der Divergenz.",
    "lex.divergence.inv": "Der Indikator zieht mit dem nächsten Hoch nach.",
    "lex.divergence.label.0": "Hoch",
    "lex.divergence.label.1": "höheres Hoch · RSI tiefer",
    "lex.hiddenDivergence.name": "Versteckte Divergenz",
    "lex.hiddenDivergence.tag": "Fortsetzungs-Divergenz",
    "lex.hiddenDivergence.desc": "Im Aufwärtstrend macht der Preis ein höheres Tief, der Indikator ein tieferes: der Rücklauf ist schwach, der Trend läuft eher weiter.",
    "lex.hiddenDivergence.conf": "Schluss über dem letzten Hoch des Rücklaufs.",
    "lex.hiddenDivergence.inv": "Schluss unter dem höheren Tief.",
    "lex.macd.name": "MACD",
    "lex.macd.tag": "Abstand zweier Durchschnitte",
    "lex.macd.desc": "Differenz von EMA 12 und EMA 26 mit Signallinie (EMA 9): Kreuzungen und die Nulllinie zeigen den Wechsel des Momentums.",
    "lex.macd.conf": "Kreuzung in Trendrichtung, gestützt durch Struktur.",
    "lex.macd.inv": "Kreuzungen in einer Seitwärtsphase: meist Rauschen.",
    "lex.stochastic.name": "Stochastik",
    "lex.stochastic.tag": "Schluss in der Spanne",
    "lex.stochastic.desc": "Wo der Schluss in der Spanne der letzten n Kerzen liegt (0–100): über 80 nahe am Hoch, unter 20 nahe am Tief.",
    "lex.stochastic.conf": "Kreuzung der Linien aus dem Extrembereich zurück, an einer Zone.",
    "lex.stochastic.inv": "Im Trend bleibt sie lange im Extrem.",
    "lex.vwap.name": "VWAP",
    "lex.vwap.tag": "Volumengewichteter Tagesdurchschnitt",
    "lex.vwap.desc": "Der Durchschnittspreis des Tages, gewichtet nach Volumen: darüber haben die Käufer des Tages im Schnitt Gewinn, er dient als Tages-Gleichgewicht.",
    "lex.vwap.conf": "Rücklauf zum VWAP hält in Trendrichtung.",
    "lex.vwap.inv": "Mehrfaches Kreuzen: Seitwärtstag.",
    "lex.vwap.level.0": "VWAP",
    "lex.bollingerSqueeze.name": "Bollinger-Squeeze",
    "lex.bollingerSqueeze.tag": "Enge Bänder vor dem Ausbruch",
    "lex.bollingerSqueeze.desc": "Die Bänder (SMA 20 ± 2 Standardabweichungen) ziehen sich zusammen: die Schwankung ist gering, ein Ausbruch steht oft bevor.",
    "lex.bollingerSqueeze.conf": "Schluss außerhalb eines Bandes nach dem Squeeze.",
    "lex.bollingerSqueeze.inv": "Rückkehr ins Band: Fehlausbruch.",
    "lex.bollingerSqueeze.label.0": "Squeeze",
    "lex.adx.name": "ADX",
    "lex.adx.tag": "Trendstärke, nicht Richtung",
    "lex.adx.desc": "Misst, wie stark ein Trend ist (0–100): über 25 ein Trend, darunter eher Seitwärts. Die Richtung zeigen +DI und −DI.",
    "lex.adx.conf": "Steigender ADX über 25 mit der Richtung von +DI/−DI.",
    "lex.adx.inv": "Fallender ADX: der Trend lässt nach.",
    "lex.volumeProfile.name": "Volumenprofil",
    "lex.volumeProfile.tag": "Volumen je Preis",
    "lex.volumeProfile.desc": "Zeigt, bei welchen Preisen am meisten gehandelt wurde: der Point of Control (POC) und die Value Area (70 % des Volumens) wirken als Magnet und Level.",
    "lex.volumeProfile.conf": "Reaktion an Rand der Value Area oder Rücklauf zum POC.",
    "lex.volumeProfile.inv": "Schluss außerhalb der Value Area und Akzeptanz dort.",
    "lex.volumeProfile.level.0": "VAH",
    "lex.volumeProfile.level.1": "POC",
    "lex.volumeProfile.level.2": "VAL",
    "lex.marketPhases.name": "Trend, Range, Expansion",
    "lex.marketPhases.tag": "Die Phasen des Markts",
    "lex.marketPhases.desc": "Märkte wechseln zwischen Seitwärtsphase (Range), Ausbruch mit großer Bewegung (Expansion) und Trend; jede Phase braucht andere Setups.",
    "lex.marketPhases.conf": "Range: Kanten handeln. Expansion/Trend: Rückläufe in Trendrichtung.",
    "lex.marketPhases.inv": "Trend-Setups in einer Range und umgekehrt.",
    "lex.marketPhases.label.0": "Range",
    "lex.marketPhases.label.1": "Expansion",
    "lex.wyckoffAccumulation.name": "Wyckoff-Akkumulation",
    "lex.wyckoffAccumulation.tag": "Kaufen in der Range",
    "lex.wyckoffAccumulation.desc": "Nach einem Fall pendelt der Kurs; ein letzter Stich unter das Tief (Spring) holt Stops, danach bricht der Kurs nach oben (Sign of Strength).",
    "lex.wyckoffAccumulation.conf": "Spring mit Schluss zurück in die Range, dann Schluss über den Widerstand.",
    "lex.wyckoffAccumulation.inv": "Schlüsse unter dem Spring-Tief.",
    "lex.wyckoffAccumulation.label.0": "Spring",
    "lex.wyckoffAccumulation.label.1": "SOS",
    "lex.wyckoffAccumulation.level.0": "Widerstand",
    "lex.wyckoffDistribution.name": "Wyckoff-Distribution",
    "lex.wyckoffDistribution.tag": "Verkaufen in der Range",
    "lex.wyckoffDistribution.desc": "Nach einem Anstieg pendelt der Kurs; ein letzter Stich über das Hoch (Upthrust) holt Stops, danach bricht der Kurs nach unten (Sign of Weakness).",
    "lex.wyckoffDistribution.conf": "Upthrust mit Schluss zurück in die Range, dann Schluss unter die Unterstützung.",
    "lex.wyckoffDistribution.inv": "Schlüsse über dem Upthrust-Hoch.",
    "lex.wyckoffDistribution.label.0": "Upthrust",
    "lex.wyckoffDistribution.label.1": "SOW",
    "lex.wyckoffDistribution.level.0": "Unterstützung",
    "lex.elliottWaves.name": "Elliott-Wellen",
    "lex.elliottWaves.tag": "Fünf Wellen mit dem Trend",
    "lex.elliottWaves.desc": "Ein Trend in fünf Wellen (1, 3, 5 mit, 2 und 4 gegen ihn), danach eine Korrektur in drei. Die Zählung ist oft erst im Rückblick eindeutig, die Belege sind schwach.",
    "lex.elliottWaves.conf": "Welle 4 bleibt über dem Hoch von Welle 1.",
    "lex.elliottWaves.inv": "Welle 2 fällt unter den Anfang von Welle 1.",
    "lex.elliottWaves.label.0": "1",
    "lex.elliottWaves.label.1": "2",
    "lex.elliottWaves.label.2": "3",
    "lex.elliottWaves.label.3": "4",
    "lex.elliottWaves.label.4": "5",
    "lex.dailyLossLimit.name": "Tagesverlustlimit",
    "lex.dailyLossLimit.tag": "Schluss für heute",
    "lex.dailyLossLimit.desc": "Ein vorher festgelegter Höchstverlust je Tag (z. B. 2 R); ist er erreicht, wird nicht mehr gehandelt. Belegt: Wer nach Verlusten das Risiko erhöht, schneidet deutlich schlechter ab.",
    "lex.drawdown.name": "Drawdown",
    "lex.drawdown.tag": "Rückgang vom Hoch",
    "lex.drawdown.desc": "Der Verlust vom höchsten Kontostand bis zum tiefsten danach; auch gute Strategien haben Serien von Verlusten, die Größe muss das aushalten.",
    "lex.riskOfRuin.name": "Risk of Ruin",
    "lex.riskOfRuin.tag": "Wahrscheinlichkeit des Totalverlusts",
    "lex.riskOfRuin.desc": "Wie wahrscheinlich ein Konto bei gegebener Trefferquote, CRV und Risiko je Trade auf null fällt; schon kleine Risiken je Trade senken sie stark.",
    "lex.riskOfRuin.list.0": "1 % je Trade: selbst lange Verlustserien bleiben überlebbar",
    "lex.riskOfRuin.list.1": "5 % je Trade: zehn Verluste in Folge kosten rund 40 %",
    "lex.correlation.name": "Korrelation",
    "lex.correlation.tag": "Wenn mehrere Trades einer sind",
    "lex.correlation.desc": "Gleichzeitige Positionen in zusammenhängenden Märkten (z. B. Gold long, DXY short) sind ein größeres Risiko als es scheint.",
    "lex.orderTypes.name": "Ordertypen",
    "lex.orderTypes.tag": "Wie eine Order ausgeführt wird",
    "lex.orderTypes.desc": "Market sofort zum aktuellen Kurs; Limit nur zum genannten Kurs oder besser; Stop wird beim Erreichen zur Market-Order; Stop-Limit wird zur Limit-Order.",
    "lex.orderTypes.list.0": "Market: sicher gefüllt, Preis offen (Slippage)",
    "lex.orderTypes.list.1": "Limit: Preis sicher, Füllung offen",
    "lex.orderTypes.list.2": "Stop: Ausbruch handeln oder Verlust begrenzen",
    "lex.orderTypes.list.3": "Stop-Limit: kein Fill um jeden Preis — bei Gaps evtl. gar keiner",
    "lex.trailingStop.name": "Trailing Stop",
    "lex.trailingStop.tag": "Der Stop zieht mit",
    "lex.trailingStop.desc": "Der Stop folgt dem Kurs in festem Abstand oder unter jedes neue Tief; er sichert Gewinne, beendet den Trade aber auch bei normalen Rückläufen.",
    "lex.trailingStop.level.0": "Trailing Stop",
    "lex.partialProfits.name": "Teilgewinne & Skalieren",
    "lex.partialProfits.tag": "Position in Teilen schließen",
    "lex.partialProfits.desc": "Ein Teil der Position wird am ersten Ziel geschlossen, der Rest läuft weiter; das glättet die Ergebnisse, kostet aber Gewinn bei großen Läufen.",
    "lex.fomo.name": "FOMO",
    "lex.fomo.tag": "Angst, etwas zu verpassen",
    "lex.fomo.desc": "Einstieg, weil der Kurs schon läuft, nicht weil das Setup steht — meist spät, mit schlechtem CRV.",
    "lex.revengeTrading.name": "Revenge Trading",
    "lex.revengeTrading.tag": "Den Verlust zurückholen wollen",
    "lex.revengeTrading.desc": "Nach einem Verlust sofort größer oder ohne Setup handeln; Studien zeigen: genau diese Trader schneiden am schlechtesten ab.",
    "lex.overtrading.name": "Overtrading",
    "lex.overtrading.tag": "Zu viele Trades",
    "lex.overtrading.desc": "Handeln aus Langeweile oder Gewohnheit: jeder Trade kostet Spread, und schwache Setups verwässern die guten.",
    "lex.dispositionEffect.name": "Disposition-Effekt",
    "lex.dispositionEffect.tag": "Gewinner zu früh, Verlierer zu spät",
    "lex.dispositionEffect.desc": "Die Neigung, Gewinne schnell mitzunehmen und Verluste laufen zu lassen; so wird der Durchschnittsverlust größer als der Durchschnittsgewinn.",
  },
};
